// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * MapLibre GL JS globe renderer.
 *
 * Wraps MapLibre with globe projection, NASA GIBS Blue Marble + Black Marble
 * raster tile sources, day/night custom layer, and vector labels/boundaries.
 */

import * as maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// AYNI (MapLibre 6, ESM only): bundlers need the worker's URL given once; Vite builds it with `?worker&url`.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'

import type { Map as MaplibreMap, StyleSpecification, CustomLayerInterface } from 'maplibre-gl'
import { cameraUpdateHook, isOccluded, setCameraUpdateHook, styleReady } from './maplibreInternals'
import { FLAT_PROJECTION, GIBS_4326_PROTOCOL, mercatorOnFlatUrl, registerFlatProjection } from './flatMap'
import { createEarthTileLayer, computeSunLightPosition, type EarthTileLayerControl, type LayerReveal, type MapLayerImages } from './earthTileLayer'
import { isEarthBody } from './datasetOverlayOptions'
import { offEarthBody } from './offEarthBody'
import { ringSystemOf } from './planetRings'
import { createPlanetRingsMapLayer, PLANET_RINGS_MAP_LAYER_ID, type PlanetRingsMapLayer } from './planetRingsMapLayer'
import type {
  Dataset,
  DatasetOverlayOptions,
  GlobeRenderer,
  MapViewContext,
  VideoTextureHandle,
} from '../types'
import { setDatasetCreditsSource } from '../ui/creditsPanel'
import { getSharedLumaSampler, probeSourceSize, type LumaSnapshot } from './glLumaSampler'
import { deliveredSizeMatches } from './valueFidelity'
import { codesAtTexels, OUTSIDE_CODE, texelsAlong } from './research/hovmoller'
import { DEFAULT_DISPLAY, type ColorScaleDisplay } from './colorScaleDisplay'
import { contourSetToGeoJson, type ContourLevel } from './datasetContours'
import { boundsRing, cropSnapshot, linePath, parallelSpanDeg, type LinePath, type TransectEndpoints } from './datasetStats'
import { densifyTrack } from './measure/followExtremes'
import type { ColorScale } from '../types/color-scale'
import {
  probeDatasetValue,
  type ProbeReading,
  type ProbeSource,
} from './datasetProbe'
import { getSunPosition } from '../utils/time'
import { dropStaleCameraCopy, GLOBE_MAX_ZOOM, isFineGrid, setFineReason } from './globeZoomCeiling'
import { ALWAYS_SPHERE_PROJECTION, CITY_PACKS, CITY_TERRAIN_OFF_ZOOM, cityMaxPitch, cityPackAt, cityTerrainExaggeration, inDeepBox, isCityCloseup, isCitySphereFix } from './cityCloseup'
import { installSphereCloseupFix } from './sphereCloseupFix'
import { isDataDrape, watchDataDrape } from './dataDrape'
import { logger } from '../utils/logger'
import { isMobile } from '../utils/deviceCapability'
import { preloadLowZoomTiles } from './tilePreloader'
import { GIBS_ATTRIBUTION } from './gibsCredit'
import { isVectorMapLayerId } from './vector/vectorMapLayer'
import { pinMarkLabel } from './measure/pinNames'
import { emitCameraSettled, emit } from '../analytics'

/** Screen-space slack, in CSS pixels, for the "is the pointer on the
 *  sphere?" round-trip test. Generous enough to survive projection
 *  rounding at the limb, tight enough to exclude the empty corners. */
const ON_GLOBE_TOLERANCE_PX = 2

// --- GIBS tile endpoints ---
const BLUE_MARBLE_TILES = [
  '/api/tile/BlueMarble_NextGeneration/default/2004-08/GoogleMapsCompatible_Level8/{z}/{y}/{x}.jpg',
]
const BLACK_MARBLE_TILES = [
  '/api/tile/VIIRS_Black_Marble/default/2016-01-01/GoogleMapsCompatible_Level8/{z}/{y}/{x}.png',
]
const GIBS_MAX_ZOOM = 8
// AYNI (2026-10-19): the same imagery on the flat map, from GIBS's geographic tiles (flatMap.ts),
// 500 m tile matrix, levels 0–7.
const FLAT_BLUE_MARBLE = `${GIBS_4326_PROTOCOL}://BlueMarble_NextGeneration/2004-08/500m/jpg/7/{z}/{x}/{y}`
const FLAT_BLACK_MARBLE = `${GIBS_4326_PROTOCOL}://VIIRS_Black_Marble/2016-01-01/500m/png/7/{z}/{x}/{y}`

maplibregl.setWorkerUrl(maplibreWorkerUrl)

// --- Default camera ---
const DEFAULT_CENTER: [number, number] = [-95, 38]
const DEFAULT_ZOOM = 2.3
// Zoom limits: ~0.5 shows the full globe, ~8 is the max detail for GIBS tiles.
// AYNI: keep MAX_ZOOM under 10. MapLibre's `globe` projection turns into
// Mercator between zoom 10 and 12, and this app never shows Mercator (owner,
// 2026-10-03); under 10 the globe is a globe at every zoom.
const MIN_ZOOM = 0.5
/** How far the camera may tilt (city close-up preview: less, close over a pack, `cityMaxPitch`). */
const MAX_PITCH = 85
/** AYNI: also the ceiling a globe returns to when no own layer or fine-grid dataset lets it come closer (decision CR-D3, `globeZoomCeiling`). */
export const MAX_ZOOM = GLOBE_MAX_ZOOM
// Approximate conversion: Three.js camera.z [1.15, 3.6] → MapLibre zoom [8, 0.5]
// altitude (km) → zoom: Earth radius ≈ 6371 km, each zoom level halves the view
const EARTH_RADIUS_KM = 6371

// --- Street map (OpenFreeMap, OpenMapTiles schema) and terrain (Terrarium) ---
// AYNI (owner rule, 7 October 2026): both come through AYNI's own route,
// `functions/api/map/[[path]].ts`, edge-cached like the GIBS tiles, so the
// browser never contacts OpenFreeMap or Amazon S3. Absolute URLs, because
// MapLibre fetches vector and terrain tiles from a web worker.
function mapApiBase(): string {
  return `${globalThis.location?.origin ?? ''}/api/map`
}

/**
 * The renderer's projection, as telemetry names it. AYNI (2026-10-03):
 * only the globe. MapLibre's flat view is Mercator, which this app never
 * uses (owner); flat world maps are Equal Earth, drawn by
 * `src/ui/equalEarthMap.ts`. Upstream's catalog Map view mounted a
 * second renderer in Mercator; that option is gone. */
export type MapRendererProjection = 'globe'

/**
 * Style factory for the globe.
 *
 * AYNI: MapLibre's `globe` turns into Mercator between zoom 10 and 12;
 * `MAX_ZOOM` (8) keeps the camera short of that, so the globe never
 * becomes Mercator. With the city close-up (`cityCloseup`, on unless
 * `?cityPreview=0`) the projection is the vertical-perspective globe at
 * every zoom instead, and each city pack (Catalonia, the Netherlands) gets
 * its photos and terrain (cityCloseup.ts). MapLibre's own sphere left parts
 * of the view undrawn past zoom 14.5 (the blurriness once seen when
 * `vertical-perspective` was first tried); sphereCloseupFix.ts corrects it.
 */
function createGlobeStyle(cityCloseup = false): StyleSpecification {
  const initSun = getSunPosition(new Date())
  return {
    version: 8,
    name: 'sos-globe',
    projection: cityCloseup ? ALWAYS_SPHERE_PROJECTION : { type: 'globe' },
    glyphs: `${mapApiBase()}/fonts/{fontstack}/{range}.pbf`,
    sources: {
      'blue-marble': {
        type: 'raster',
        tiles: BLUE_MARBLE_TILES,
        tileSize: 256,
        maxzoom: GIBS_MAX_ZOOM,
        attribution: GIBS_ATTRIBUTION,
      },
      'black-marble': {
        type: 'raster',
        tiles: BLACK_MARBLE_TILES,
        tileSize: 256,
        maxzoom: GIBS_MAX_ZOOM,
        attribution: GIBS_ATTRIBUTION,
      },
      'openmaptiles': {
        type: 'vector',
        url: `${mapApiBase()}/planet`,
        attribution: '© OpenMapTiles © OpenStreetMap',
      },
      'terrain-dem': {
        type: 'raster-dem',
        tiles: [`${mapApiBase()}/terrain/{z}/{x}/{y}.png`],
        tileSize: 256,
        maxzoom: 14,
        encoding: 'terrarium',
        attribution: 'Mapzen Terrain',
      },
      ...(cityCloseup ? CITY_SOURCES() : {}),
    },
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: { 'background-color': '#000000', 'background-opacity': 0 },
      },
      {
        id: 'black-marble-layer',
        type: 'raster',
        source: 'black-marble',
        paint: { 'raster-opacity': 1 },
      },
      {
        id: 'blue-marble-layer',
        type: 'raster',
        source: 'blue-marble',
        paint: { 'raster-opacity': 1 },
      },
      ...(cityCloseup ? CITY_LAYERS : []),
      // --- Vector layers (hidden by default, toggle-able) ---
      {
        id: 'coastline-halo',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'water',
        filter: ['==', 'class', 'ocean'],
        layout: { visibility: 'none' },
        paint: {
          'line-color': 'rgba(0, 0, 0, 0.22)',
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1.4, 5, 2.6, 8, 3.2],
        },
      },
      {
        id: 'coastline',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'water',
        filter: ['==', 'class', 'ocean'],
        layout: { visibility: 'none' },
        paint: {
          'line-color': 'rgba(255, 255, 255, 0.46)',
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.45, 5, 1.1, 8, 1.6],
        },
      },
      {
        id: 'boundaries-halo',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary',
        filter: ['all', ['==', 'admin_level', 2], ['!=', 'maritime', 1]],
        layout: { visibility: 'none' },
        paint: {
          'line-color': 'rgba(0, 0, 0, 0.24)',
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1.6, 5, 3.2, 8, 4],
        },
      },
      {
        id: 'boundaries',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary',
        filter: ['all', ['==', 'admin_level', 2], ['!=', 'maritime', 1]],
        layout: { visibility: 'none' },
        paint: {
          'line-color': 'rgba(255, 255, 255, 0.52)',
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.6, 5, 1.4, 8, 2],
        },
      },
      {
        id: 'country-labels',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'place',
        filter: ['==', 'class', 'country'],
        layout: {
          visibility: 'none',
          'text-field': '{name:latin}',
          'text-font': ['Open Sans Bold'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 5, 16, 8, 20],
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.1,
          'text-max-width': 8,
          'text-pitch-alignment': 'map',
          'text-rotation-alignment': 'map',
        },
        paint: {
          'text-color': 'rgba(255, 255, 255, 0.85)',
          'text-halo-color': 'rgba(0, 0, 0, 0.7)',
          'text-halo-width': 1.5,
        },
      },
      {
        id: 'city-labels',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'place',
        filter: ['all', ['==', 'class', 'city'], ['>=', 'rank', 1], ['<=', 'rank', 6]],
        layout: {
          visibility: 'none',
          'text-field': '{name:latin}',
          'text-font': ['Open Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 3, 9, 6, 13, 8, 16],
          'text-max-width': 8,
          'text-pitch-alignment': 'map',
          'text-rotation-alignment': 'map',
        },
        paint: {
          'text-color': 'rgba(220, 220, 255, 0.8)',
          'text-halo-color': 'rgba(0, 0, 0, 0.6)',
          'text-halo-width': 1,
        },
      },
      {
        id: 'ocean-labels',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'water_name',
        filter: ['==', '$type', 'Point'],
        layout: {
          visibility: 'none',
          'text-field': '{name:latin}',
          'text-font': ['Open Sans Italic'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 5, 14],
          'text-letter-spacing': 0.2,
          'text-max-width': 10,
          'text-pitch-alignment': 'map',
          'text-rotation-alignment': 'map',
        },
        paint: {
          'text-color': 'rgba(150, 200, 255, 0.6)',
          'text-halo-color': 'rgba(0, 0, 0, 0.5)',
          'text-halo-width': 1,
        },
      },
    ],
    sky: {
      'atmosphere-blend': [
        'interpolate',
        ['linear'],
        ['zoom'],
        0, 1,
        5, 1,
        7, 0,
      ],
    },
    // Initialize light to current sun position so there's no flash of
    // incorrect lighting before the earth tile layer's onAdd fires.
    light: {
      anchor: 'map',
      position: computeSunLightPosition(initSun.lat, initSun.lng),
    },
  }
}

/**
 * The city close-up's sources: each city pack's aerial
 * photos and terrain from AYNI's own bucket through the map route, limited
 * to the pack's box so MapLibre never asks for a tile outside it. Each
 * pack's source carries its own credit, so Tools → Credits names only the
 * packs on screen.
 */
function CITY_SOURCES(): StyleSpecification['sources'] {
  const sources: StyleSpecification['sources'] = {}
  for (const pack of CITY_PACKS) {
    const photo = `${mapApiBase()}/city/${pack.id}/photo/{z}/{x}/{y}.webp`
    sources[cityPhotoSource(pack.id)] = {
      type: 'raster', tiles: [photo], tileSize: 256, minzoom: 7, maxzoom: 15, bounds: pack.bounds, attribution: pack.photoCredit,
    }
    // The same photos stopping at zoom 11: MapLibre's always-sphere globe
    // sometimes picks no deep tile for the near edge of a close or tilted
    // view; this layer underneath fills such a gap with a softer photo
    // instead of the 500 m Blue Marble.
    sources[`${cityPhotoSource(pack.id)}-low`] = {
      type: 'raster', tiles: [photo], tileSize: 256, minzoom: 7, maxzoom: 11, bounds: pack.bounds, attribution: pack.photoCredit,
    }
    // Zoom 16–17 over the pack's city only (Barcelona, Rotterdam), on top.
    if (pack.deep) {
      sources[`${cityPhotoSource(pack.id)}-deep`] = {
        type: 'raster', tiles: [photo], tileSize: 256, minzoom: 16, maxzoom: pack.deep.maxzoom, bounds: pack.deep.bounds, attribution: pack.photoCredit,
      }
    }
    sources[cityTerrainSource(pack.id)] = {
      type: 'raster-dem',
      tiles: [`${mapApiBase()}/city/${pack.id}/terrain/{z}/{x}/{y}.png`],
      tileSize: 256,
      maxzoom: pack.terrainMaxZoom,
      bounds: pack.bounds,
      encoding: 'mapbox',
      attribution: pack.terrainCredit,
    }
  }
  return sources
}

/** AYNI (2026-10-20): every city photo source with its tiles and levels (the style's, and the flat map's cut of them). */
function cityPhotoTiles(): { source: string; template: string; minzoom: number; maxzoom: number }[] {
  return CITY_PACKS.flatMap((pack) => {
    const template = `${mapApiBase()}/city/${pack.id}/photo/{z}/{x}/{y}.webp`
    const id = cityPhotoSource(pack.id)
    return [
      { source: id, template, minzoom: 7, maxzoom: 15 },
      { source: `${id}-low`, template, minzoom: 7, maxzoom: 11 },
      ...(pack.deep ? [{ source: `${id}-deep`, template, minzoom: 16, maxzoom: pack.deep.maxzoom }] : []),
    ]
  })
}

/** A city pack's photo source (its zoom-11 underlay adds `-low`, its zoom 16–17 city `-deep`). */
export function cityPhotoSource(packId: string): string {
  return `city-photo-${packId}`
}

/** A city pack's terrain source: MapLibre drapes one terrain at a time, the pack's under the camera. */
export function cityTerrainSource(packId: string): string {
  return `city-terrain-${packId}`
}

/** Each pack's photos over the Blue Marble, faded in from zoom 7 to 9 (the Blue Marble is 500 m a pixel). */
const CITY_LAYERS: StyleSpecification['layers'] = CITY_PACKS.flatMap((pack): StyleSpecification['layers'] => [
  {
    id: `${cityPhotoSource(pack.id)}-low-layer`,
    type: 'raster',
    source: `${cityPhotoSource(pack.id)}-low`,
    paint: { 'raster-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 9, 1], 'raster-fade-duration': 0 },
  },
  {
    id: `${cityPhotoSource(pack.id)}-layer`,
    type: 'raster',
    source: cityPhotoSource(pack.id),
    paint: { 'raster-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 9, 1], 'raster-fade-duration': 150 },
  },
  ...(pack.deep ? [{
    id: `${cityPhotoSource(pack.id)}-deep-layer`,
    type: 'raster' as const,
    source: `${cityPhotoSource(pack.id)}-deep`,
    minzoom: 15,
    paint: { 'raster-fade-duration': 150 },
  }] : []),
])

/**
 * Module-level reference to the "active" (primary) MapRenderer instance.
 *
 * In single-viewport mode this is the only renderer. In multi-viewport mode
 * (ViewportManager) this points at whichever panel is currently primary.
 * ViewportManager is responsible for calling setActiveMapRenderer() whenever
 * the primary changes.
 *
 * Used by screenshotService so screenshot consumers (vision flow, feedback
 * form) can drive a proper triggerRepaint + once('render') cycle rather than
 * fighting the WebGL drawing-buffer preservation quirks on their own.
 */
let activeRenderer: MapRenderer | null = null

/** Get the currently active (primary) MapRenderer, if any. */
export function getActiveMapRenderer(): MapRenderer | null {
  return activeRenderer
}

/** Set the active (primary) MapRenderer. Called by ViewportManager. */
export function setActiveMapRenderer(renderer: MapRenderer | null): void {
  activeRenderer = renderer
}

/** Max dimension for a captured screenshot — keeps payload small. */
const SCREENSHOT_MAX_SIZE = 512

/** MapLibre-based globe renderer. */
export class MapRenderer implements GlobeRenderer {
  private map: MaplibreMap | null = null
  /** Detaches the lat/lng pointer handlers, so a second
   *  `setLatLngCallbacks` replaces rather than stacks them. */
  private latLngUnsubscribe: (() => void) | null = null
  /** The currently displayed dataset frame source, kept so the hover
   *  probe can read one texel out of it. */
  /** Viewing state for data-encoded datasets, re-applied whenever the
   *  earth layer is (re)created so a display chosen before the first
   *  dataset loaded is not lost. */
  private colorScaleDisplay: ColorScaleDisplay = DEFAULT_DISPLAY
  private probeSource: ProbeSource | null = null
  /** AYNI (CR-D3): the dataset whose grid sets this globe's zoom ceiling, and the listener that re-reads its size. */
  private zoomSource: ProbeSource | null = null
  private zoomSourceListener: (() => void) | null = null
  private probeOptions: DatasetOverlayOptions | null = null
  /** 1x1 scratch canvas the probe draws into. Created once; a fresh
   *  canvas per pointer event would allocate on every mouse move. */
  private container: HTMLElement | null = null
  private canvasId: string = 'globe-canvas'
  /** The projection telemetry reports (`getProjection()`): always the globe. */
  private readonly projection: MapRendererProjection = 'globe'
  /** AYNI: the city close-up (on unless `?cityPreview=0`, cityCloseup.ts), fixed for the page's life. */
  private readonly cityCloseup = isCityCloseup()
  /** Whether the camera is over a city pack, so the ceiling is the city zoom (preview only). */
  private overCityPack = false
  /** Whether the camera is over a pack's zoom 16–17 photos, so the ceiling is the deep city zoom (preview only). */
  private overDeepBox = false
  /** Whether MapLibre's sphere carries our street-zoom corrections (sphereCloseupFix.ts); without them no deep zoom. */
  private sphereFixed = false
  /** Takes the dataset off the terrain (dataDrape.ts), or null while it is not draped there. */
  private drapeStop: (() => void) | null = null
  /** The pack whose terrain is draped: the last one the camera's centre was over (preview only). */
  private terrainPack = CITY_PACKS[0].id
  /** AYNI: the pending restart after something interrupted a spin step. */
  private autoRotateResume: number | null = null
  /** AYNI: the current spin step, and when it would end undisturbed. */
  private autoRotateStep: (() => void) | null = null
  /** AYNI: the longitude auto-rotation has turned the camera through (the stars are drawn less this), and where its step last was. */
  private skyTurn = 0
  private spinFrom: number | null = null
  private autoRotateStepEnds = 0
  private autoRotating = false
  private rotationRate = 1.0 // 1.0 = default (30° per 10s)
  private earthLayer: EarthTileLayerControl | null = null
  /**
   * AYNI (multi-globe plan): the Earth's effects and the atmosphere were
   * hidden for a dataset. Asked before the map had loaded (a globe the
   * layout just added, loaded at once), the request found no layer and the
   * globe came up with the day-and-night Earth and its haze over the data;
   * the layer now takes it when it is built.
   */
  private effectsHidden = false
  private pendingTexture: HTMLCanvasElement | HTMLImageElement | null = null
  private pendingVideo: HTMLVideoElement | null = null
  /** Phase 3e: dataset overlay options buffered alongside the pending
   * texture/video so the eventual setDatasetTexture / setDatasetVideo
   * call gets the same per-dataset hints (bbox / lonOrigin /
   * isFlippedInY / celestialBody) the caller passed at request time. */
  private pendingDatasetOptions: DatasetOverlayOptions | null = null
  /** AYNI: layers asked for before the earth layer existed; applied on load. */
  private pendingMapLayers: MapLayerImages | null | undefined = undefined
  /** 0-based slot index for this renderer within its ViewportManager.
   * Reported on `camera_settled` / `map_click` so downstream queries
   * can separate primary vs secondary-panel activity. Defaults to 0
   * for single-viewport flows. */
  private slotIndex: number = 0
  /** Telemetry-only — returns the dataset id currently loaded in
   * this renderer's slot. Forwarded into `camera_settled` so
   * dashboards can split spatial-attention heatmaps by dataset
   * without a session-scoped join. Null when no dataset is loaded
   * (panel shows the default Earth). Caller-provided so MapRenderer
   * doesn't need to know about main.ts's panel-state model. */
  private getLayerId: (() => string | null) | null = null

  /**
   * Initialize the MapLibre map inside the given container element.
   * The container element is used directly as MapLibre's parent — the
   * caller is responsible for sizing and placing it in the DOM tree.
   *
   * In single-viewport mode (`main.ts`) the container is typically a
   * dedicated `<div class="map-viewport">` inside `#map-grid`. In
   * multi-viewport mode ViewportManager creates one such div per
   * panel. MapRenderer no longer creates its own wrapper.
   *
   * @param options.canvasId ID to set on the WebGL canvas element.
   * Defaults to `'globe-canvas'` for single-viewport backward compat;
   * ViewportManager passes unique IDs per panel to avoid DOM collisions.
   */
  init(
    container: HTMLElement,
    options?: {
      canvasId?: string
      slotIndex?: number
      getLayerId?: () => string | null
    },
  ): void {
    this.container = container
    this.canvasId = options?.canvasId ?? 'globe-canvas'
    this.slotIndex = options?.slotIndex ?? 0
    this.getLayerId = options?.getLayerId ?? null

    // Inject dark popup styles (idempotent — skips if already present)
    if (!document.getElementById('sos-popup-style')) {
      const style = document.createElement('style')
      style.id = 'sos-popup-style'
      style.textContent = `
        .sos-popup .maplibregl-popup-content {
          background: rgba(13,13,18,0.92);
          backdrop-filter: blur(12px);
          padding: 0;
          border-radius: 6px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.5);
        }
        .sos-popup .maplibregl-popup-tip {
          border-top-color: rgba(13,13,18,0.92);
        }
        .sos-popup .maplibregl-popup-close-button {
          color: #aaa;
          font-size: 16px;
          padding: 2px 6px;
        }
      `
      document.head.appendChild(style)
    }

    // AYNI (2026-10-19): the flat map's projection, in the workers before any GeoJSON loads.
    void registerFlatProjection()
    this.map = new maplibregl.Map({
      container,
      style: createGlobeStyle(this.cityCloseup),
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      // Stock attribution control disabled — Tools → Credits is
      // now the canonical surface for both basemap and dataset
      // attributions, sourced from `map.getStyle().sources[…].attribution`.
      // See src/ui/creditsPanel.ts for the design.
      attributionControl: false,
      // needed for captureViewContext / toDataURL. AYNI: inside canvasContextAttributes since MapLibre 6,
      // which ignores the old top-level option (the cast below hid it).
      canvasContextAttributes: { preserveDrawingBuffer: true },
      maxPitch: MAX_PITCH,
      maxTileCacheSize: isMobile() ? 750 : 2000,
    })

    if (this.cityCloseup) {
      // MapLibre's sphere at street zoom: our near plane and frustum (sphereCloseupFix.ts).
      // Its globe transform exists once the style, with the projection, has loaded.
      const map = this.map
      if (isCitySphereFix()) {
        const fix = () => {
          this.sphereFixed = installSphereCloseupFix(map as never, maplibregl.getVersion(), (lng, lat) => new maplibregl.LngLat(lng, lat), (zoom) => cityTerrainExaggeration(zoom, true))
          // The dataset on the terrain close in (dataDrape.ts), once the sphere draws right.
          if (this.sphereFixed && !this.drapeStop && isDataDrape()) {
            this.drapeStop = watchDataDrape(map, {
              wanted: () => !this.flat && this.sphereFixed && map.getTerrain() !== null && !this.datasetSuperseded && isEarthBody(this.baseLayerOptions?.celestialBody),
              requestDrape: (box, width, height) => this.earthLayer?.requestDrape(box, width, height) ?? Promise.resolve(null),
              setDrapeHole: (hole) => this.earthLayer?.setDrapeHole(hole),
              frameTime: () => this.earthLayer?.getUploadedFrameTime() ?? null,
              datasetBox: () => this.baseLayerOptions?.boundingBox ?? null,
              terrainBox: () => { const b = CITY_PACKS.find((p) => p.id === this.terrainPack)?.bounds; return b ? { w: b[0], s: b[1], e: b[2], n: b[3] } : null },
              maxPixels: () => (isMobile() ? 1024 : 2048),
              beforeLayer: () => (map.getLayer('coastline-halo') ? 'coastline-halo' : undefined),
            })
          }
        }
        map.on('style.load', fix)
        map.once('load', fix)
      }
      this.watchCityPack(map)
    }

    // Double-click/double-tap resets to default view instead of zoom in
    this.map.doubleClickZoom.disable()
    const resetView = () => this.resetView()
    this.map.on('dblclick', resetView)

    // Emit `camera_settled` after every user-driven move ends.
    // MapLibre fires `moveend` on every drag release, wheel stop,
    // and `flyTo` completion — good enough for "user parked the
    // camera here". The shared throttle in analytics/camera.ts
    // caps the emit rate to ≤ 30/min per session across 2D + VR.
    this.map.on('moveend', () => {
      if (!this.map) return
      // Skip moves driven by auto-rotate: it sweeps the centre
      // longitude on a timer (`easeTo` every 10s), and each ease
      // completion is a `moveend`. Those are globe spin, not
      // attention — emitting them paints a spurious latitude-wide
      // band of bins across the spatial heatmap. AYNI: auto-rotation
      // now stays on through a drag, so while it is on no settle is
      // emitted at all.
      if (this.autoRotating) return
      const center = this.map.getCenter()
      emitCameraSettled({
        slot_index: String(this.slotIndex),
        projection: this.projection,
        center_lat: center.lat,
        center_lon: center.lng,
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
        layer_id: this.getLayerId?.() ?? '',
      })
    })

    // Emit `map_click` on every single-click (not double-click,
    // which resets the view). Classification for now is simply
    // "surface" — marker / region hit-tests land in later commits
    // once dataset overlays expose layer ids.
    this.map.on('click', (e) => {
      // Query rendered features at the click point. The global
      // `click` event doesn't attach a features array — pull it
      // via queryRenderedFeatures. MapLibre returns layer-ordered
      // hits; the top-most is the visible one.
      const features = this.map?.queryRenderedFeatures(e.point) ?? []
      const top = features[0]
      const hitKind: 'surface' | 'marker' | 'feature' | 'region' = top ? 'feature' : 'surface'
      emit({
        event_type: 'map_click',
        slot_index: String(this.slotIndex),
        hit_kind: hitKind,
        hit_id: top?.id != null ? String(top.id) : '',
        lat: Math.round(e.lngLat.lat * 1000) / 1000,
        lon: Math.round(e.lngLat.lng * 1000) / 1000,
        zoom: Math.round(this.map!.getZoom() * 100) / 100,
      })
    })

    // Double-tap detection for touch (dblclick doesn't fire on touch devices)
    let lastTap = 0
    this.map.on('touchend', (e) => {
      // Only single-finger taps count as double-tap
      if (e.originalEvent.touches.length > 0) return
      const now = Date.now()
      if (now - lastTap < 350) {
        e.preventDefault()
        resetView()
        lastTap = 0
      } else {
        lastTap = now
      }
    })

    // Accessibility
    const canvas = this.map.getCanvas()
    canvas.setAttribute('role', 'img')
    canvas.setAttribute('aria-label', 'Interactive 3D globe visualization')
    canvas.id = this.canvasId

    // Add earth tile layer (2d — renders below labels), then skybox layer
    // (3d — renders after everything, uses depth test for stars behind globe).
    // Move label layers above the earth tile layer so they aren't darkened.
    //
    // `load` is the right signal and stays the primary one, but it is
    // not a *guaranteed* one: see `buildGlobeLayers` for why there is a
    // deadline behind it.
    this.map.on('load', () => this.buildGlobeLayers(container, 'load'))
    this.styleDeadline = setTimeout(
      () => this.buildGlobeLayers(container, 'deadline'),
      MapRenderer.STYLE_READY_DEADLINE_MS,
    )
  }

  /**
   * Longest we wait for MapLibre's `load` before building the globe's
   * own layers anyway.
   *
   * Generous, because the happy path must never take this branch on a
   * merely-slow connection — `load` firing at eight seconds is fine and
   * common, and building early would only add a redundant code path to
   * every session.
   */
  private static readonly STYLE_READY_DEADLINE_MS = 8_000

  private globeLayersBuilt = false
  private styleDeadline: ReturnType<typeof setTimeout> | null = null

  /**
   * Build the earth tile layer, the capture layer and the skybox, and
   * flush any dataset texture buffered before they existed.
   *
   * **Why this is not simply the `load` handler.** MapLibre fires `load`
   * only once *every source in the style* reports loaded, and the style
   * declares `openmaptiles` by TileJSON `url` — a network fetch to
   * OpenFreeMap that must resolve before the map is considered loaded.
   * That source exists for the labels and boundaries overlays, which are
   * **off by default** and live behind a Tools toggle. So a slow or
   * unreachable third-party basemap host could hold the globe's own
   * layers hostage indefinitely: no earth layer, so a dataset already
   * fetched and decoded sat in `pendingTexture` forever, and the globe
   * showed bare raster tiles with no day/night, no atmosphere, and — for
   * a data-encoded row — no probe source and therefore no value readout
   * and no Analyze panel. An optional decoration must not be able to
   * cost the visitor the thing they actually asked for.
   *
   * Observed in the visual-report harness, where OpenFreeMap is
   * unreachable: `load` never fired, and a data-encoded scene reported
   * "no dataset carrying values" against a globe that had one.
   *
   * The deadline path is safe because `addLayer` needs only the style
   * *parsed*, not every source *loaded*. This renderer hands MapLibre a
   * style **object** in the `Map` constructor (`style:
   * createGlobeStyle(...)`), so there is no style-document fetch to wait
   * on — construction applies it and MapLibre's internal loaded flag,
   * the one `addLayer` checks, is set from there. `Map#loaded()` is the
   * separate question of whether every *source* has finished, and that
   * is exactly the wait being bypassed.
   *
   * Runs once; whichever trigger arrives first wins.
   */
  private buildGlobeLayers(container: HTMLElement, trigger: 'load' | 'deadline'): void {
    if (this.globeLayersBuilt || !this.map) return
    this.globeLayersBuilt = true
    if (this.styleDeadline) {
      clearTimeout(this.styleDeadline)
      this.styleDeadline = null
    }
    if (trigger === 'deadline') {
      logger.warn(
        '[MapRenderer] Map `load` did not fire within ' +
          `${MapRenderer.STYLE_READY_DEADLINE_MS}ms — a style source is still ` +
          'pending. Building the globe layers anyway so the dataset is not ' +
          'held up by the basemap.',
      )
    }
    logger.info(`[MapRenderer] Map loaded with ${this.projection} projection`)

    // Collapse the compact attribution control so it doesn't cover the auto-rotate button
    const attrib = container.querySelector('.maplibregl-ctrl-attrib.maplibregl-compact')
    attrib?.classList.remove('maplibregl-compact-show')

    this.earthLayer = createEarthTileLayer()
    // AYNI (2026-10-19): a globe switched to the flat map before its layers were built.
    this.earthLayer.setFlat(this.flat)

    // Layer order: black-marble → [capture] → blue-marble → [earth-tile] → labels → [skybox]
    // Insert capture layer between Black Marble and Blue Marble
    this.map!.addLayer(
      this.earthLayer.captureLayer as unknown as maplibregl.LayerSpecification,
      'blue-marble-layer',
    )
    // Insert main earth effects layer after Blue Marble (at end of 2d layers)
    this.map!.addLayer(this.earthLayer.layer as unknown as maplibregl.LayerSpecification)

    // AYNI (DSA 1.9): a still picture can load before the map does, and its
    // vector layers with it; they belong above the dataset, not under it.
    for (const layer of this.map!.getStyle()?.layers ?? []) {
      if (isVectorMapLayerId(layer.id)) {
        try { this.map!.moveLayer(layer.id) } catch { /* layer may be gone */ }
      }
    }

    // Move label/boundary layers above the earth tile layer
    for (const id of this.allOverlayLayerIds) {
      try { this.map!.moveLayer(id) } catch { /* layer may not exist */ }
    }

    // Add skybox as a separate 3d layer (renders after all 2d layers)
    this.map!.addLayer(this.earthLayer.skyboxLayer as unknown as maplibregl.LayerSpecification)

    // A dataset asked for the plain look before the layer existed.
    if (this.effectsHidden) {
      this.earthLayer.setVisible(false)
      try { this.map!.setSky({ 'atmosphere-blend': 0 }) } catch { /* noop */ }
    }

    // Re-apply the viewing transform before the buffered dataset, so
    // the LUT is already correct on the layer's first build rather
    // than being rebuilt a frame later — otherwise a globe restored
    // with a non-default palette flashes the publisher's ramp first.
    this.earthLayer.setColorScaleDisplay(this.colorScaleDisplay)
    this.earthLayer.setDatasetUserOpacity(this.datasetUserOpacity)

    // Apply any dataset texture/video that was buffered before the
    // layer was ready.
    //
    // The probe source is assigned here as well as on the direct
    // path. It used to be set only on the direct path, so a dataset
    // that finished loading *before* the layers were built — a fast
    // local asset, a warm cache, a fixtured scene — reached the globe
    // with no probe source at all. The value readout then reported
    // nothing for that dataset and looked exactly like a dataset with
    // nothing to report, for the rest of its life on screen.
    if (this.pendingTexture) {
      const opts = this.pendingDatasetOptions ?? undefined
      this.earthLayer.setDatasetTexture(this.pendingTexture, opts)
      this.probeSource = this.pendingTexture
      this.probeOptions = opts ?? null
      this.applyDatasetZoom(this.pendingTexture, opts ?? null)
      this.pendingTexture = null
      this.pendingDatasetOptions = null
      this.applyBaseLayerVisibility(opts)
    } else if (this.pendingVideo) {
      const opts = this.pendingDatasetOptions ?? undefined
      this.earthLayer.setDatasetVideo(this.pendingVideo, opts)
      this.probeSource = this.pendingVideo
      this.probeOptions = opts ?? null
      this.applyDatasetZoom(this.pendingVideo, opts ?? null)
      this.pendingVideo = null
      this.pendingDatasetOptions = null
      this.applyBaseLayerVisibility(opts)
    }

    if (this.pendingMapLayers !== undefined) {
      this.earthLayer.setMapLayers(this.pendingMapLayers)
      this.pendingMapLayers = undefined
    }

    logger.info('[MapRenderer] Earth tile + capture + skybox layers added, labels moved above')

    // Preload low-zoom tiles into browser/SW cache.
    // Mobile: wait for 'idle' (initial viewport tiles rendered) to avoid bandwidth contention.
    // Desktop: start immediately after layer setup.
    if (isMobile()) {
      this.map!.once('idle', () => preloadLowZoomTiles())
    } else {
      preloadLowZoomTiles()
    }
  }

  /** Return the underlying MapLibre map instance. */
  getMap(): MaplibreMap | null {
    return this.map
  }

  /** The projection telemetry stamps on `camera_settled`: the globe. */
  getProjection(): MapRendererProjection {
    return this.projection
  }

  /**
   * Register or clear the dataset-credits phantom source on this
   * panel's map. Pass a Dataset to set the credit; pass null to
   * clear it (after unload). The phantom source carries no
   * features and no layers reference it — it exists only to feed
   * its `attribution` string into MapLibre's source-attribution
   * pipeline so the Tools → Credits panel can read it back.
   *
   * See src/ui/creditsPanel.ts for the full design + composition
   * rules. Idempotent — calling with the same dataset twice is a
   * no-op beyond the redundant remove/add.
   */
  setDatasetCredits(dataset: Dataset | null): void {
    setDatasetCreditsSource(this.map, dataset)
  }

  /** Return the map canvas element for screenshot capture. */
  getCanvas(): HTMLCanvasElement | null {
    return this.map?.getCanvas() ?? null
  }

  /**
   * Force MapLibre to render a fresh frame and resolve once the render
   * event fires. Used as a prelude to screenshot capture so the WebGL
   * drawing buffer is guaranteed to be populated when we read pixels.
   *
   * Falls back to a short timeout if the render event never fires
   * (map hidden, disposed, or otherwise quiescent) so screenshot
   * callers don't hang forever.
   */
  async triggerFreshRender(): Promise<void> {
    const map = this.map
    if (!map) return
    const RENDER_WAIT_TIMEOUT_MS = 1000
    await new Promise<void>((resolve) => {
      let settled = false
      const timeoutId = window.setTimeout(() => {
        if (settled) return
        settled = true
        resolve()
      }, RENDER_WAIT_TIMEOUT_MS)
      map.once('render', () => {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        resolve()
      })
      map.triggerRepaint()
    })
  }

  /**
   * Capture the current globe view as a JPEG data URL.
   *
   * A naive `canvas.toDataURL()` is unreliable on MapLibre — even with
   * `preserveDrawingBuffer: true`, the WebGL drawing buffer can be
   * cleared between frames when the map is idle, producing a black
   * image. We force a fresh repaint and wait for MapLibre to finish
   * rendering before reading pixels.
   *
   * @param options.maxSize Max dimension (px) on the longer edge of the
   * output image. Defaults to SCREENSHOT_MAX_SIZE (512) for the vision
   * flow. Pass `Infinity` (or a very large number) to skip the
   * downsample step — useful when compositing into a larger capture.
   *
   * Returns null if the map isn't initialized or the capture fails.
   */
  async captureScreenshot(options?: { maxSize?: number }): Promise<string | null> {
    const map = this.map
    if (!map) return null
    const maxSize = options?.maxSize ?? SCREENSHOT_MAX_SIZE
    try {
      await this.triggerFreshRender()
      const canvas = map.getCanvas()
      const { width, height } = canvas
      const scale = Math.min(1, maxSize / Math.max(width, height))
      if (scale < 1) {
        const offscreen = document.createElement('canvas')
        offscreen.width = Math.round(width * scale)
        offscreen.height = Math.round(height * scale)
        const ctx = offscreen.getContext('2d')
        if (!ctx) return canvas.toDataURL('image/jpeg', 0.6)
        ctx.drawImage(canvas, 0, 0, offscreen.width, offscreen.height)
        return offscreen.toDataURL('image/jpeg', 0.6)
      }
      return canvas.toDataURL('image/jpeg', 0.6)
    } catch (err) {
      logger.warn('[MapRenderer] captureScreenshot failed:', err)
      return null
    }
  }

  // --- Navigation ---

  /**
   * Fly the camera to a geographic location.
   * The third parameter is altitude in km (matching Three.js convention).
   * It's converted to a MapLibre zoom level.
   */
  flyTo(lat: number, lon: number, altitude?: number): Promise<void> {
    if (!this.map) return Promise.resolve()

    let zoom = this.map.getZoom()
    if (altitude !== undefined) {
      // Convert altitude (km) to MapLibre zoom level
      // At zoom 0, the view covers ~40,000 km. Each zoom level halves the view.
      // altitude ≈ EARTH_RADIUS_KM * 2 / 2^zoom → zoom ≈ log2(EARTH_RADIUS_KM * 2 / altitude)
      zoom = Math.max(MIN_ZOOM, Math.min(this.map.getMaxZoom(),
        Math.log2(EARTH_RADIUS_KM * 2 / Math.max(altitude, 1))))
    }

    return new Promise<void>(resolve => {
      this.map!.once('moveend', () => resolve())
      this.map!.flyTo({
        center: [lon, lat],
        zoom,
        duration: 2500,
      })
    })
  }

  /**
   * Fly the camera to fit a bounding box.
   * bounds: [west, south, east, north] in degrees.
   */
  fitBounds(
    bounds: [number, number, number, number],
    options?: { padding?: number | { top: number; bottom: number; left: number; right: number }; duration?: number },
  ): void {
    let [west, south, east, north] = bounds
    // Normalize antimeridian-crossing bounds (west > east) by wrapping east
    if (west > east) east += 360
    this.map?.fitBounds(
      [[west, south], [east, north]],
      {
        padding: options?.padding ?? 50,
        duration: options?.duration ?? 2500,
      },
    )
  }

  // --- Label & boundary layer toggles ---

  private readonly allOverlayLayerIds = ['coastline-halo', 'coastline', 'boundaries-halo', 'boundaries', 'country-labels', 'city-labels', 'ocean-labels']
  private readonly labelOnlyIds = ['country-labels', 'city-labels', 'ocean-labels']
  private readonly boundaryOnlyIds = ['coastline-halo', 'coastline', 'boundaries-halo', 'boundaries']

  /** Show or hide label layers only (country, city, ocean names). */
  toggleLabels(visible?: boolean): boolean {
    // AYNI: the style itself, not every tile (`isStyleLoaded` waits for those too, and a switch pressed meanwhile was lost).
    if (!this.map || !styleReady(this.map)) return false
    let firstLayer: unknown
    try { firstLayer = this.flat ? this.flatHeldVisibility.get('country-labels') : this.map.getLayoutProperty('country-labels', 'visibility') } catch { /* style not ready */ }
    const show = visible ?? (firstLayer === 'none' || firstLayer === undefined)
    const vis = show ? 'visible' : 'none'
    for (const id of this.labelOnlyIds) {
      // AYNI: the label tiles are Web Mercator; on the flat map the choice is kept for the globe
      // and the flat labels show it (syncFlatLabels).
      if (this.flat) this.flatHeldVisibility.set(id, vis)
      else try { this.map.setLayoutProperty(id, 'visibility', vis) } catch { /* noop */ }
    }
    if (this.flat) this.syncFlatLabels(this.map)
    return show
  }

  /** Show or hide transparent country boundary + coastline reference lines. */
  toggleBoundaries(visible?: boolean): boolean {
    if (!this.map || !styleReady(this.map)) return false
    let current: unknown
    try { current = this.flat ? this.flatHeldVisibility.get('boundaries') : this.map.getLayoutProperty('boundaries', 'visibility') } catch { /* style not ready */ }
    const show = visible ?? (current === 'none' || current === undefined)
    const vis = show ? 'visible' : 'none'
    for (const id of this.boundaryOnlyIds) {
      if (this.flat) {
        this.flatHeldVisibility.set(id, vis)
        continue
      }
      try { this.map.setLayoutProperty(id, 'visibility', vis) } catch { /* noop */ }
    }
    return show
  }

  // --- Markers & popups ---

  private markers: maplibregl.Marker[] = []

  /** Add a marker at the given coordinates with an optional popup label.
   *  The popup opens automatically so the label is immediately visible.
   *  AYNI: `color` for a Khipu's pins, in the four pin colours. */
  addMarker(lat: number, lng: number, label?: string, color?: string): maplibregl.Marker | null {
    if (!this.map) return null
    const marker = new maplibregl.Marker({ color: color ?? '#facc15' }) // --color-brand-end
      .setLngLat([lng, lat])
    if (label) {
      const popupContent = document.createElement('div')
      popupContent.style.cssText = 'color:#fff;background:rgba(13,13,18,0.92);padding:6px 10px;border-radius:6px;font:13px/1.4 system-ui,sans-serif;white-space:nowrap;'
      popupContent.textContent = label
      // AYNI: opening a label must not take the keyboard focus from the panel that put it there.
      marker.setPopup(new maplibregl.Popup({ offset: 25, className: 'sos-popup', focusAfterOpen: false }).setDOMContent(popupContent))
    }
    marker.addTo(this.map)
    // Auto-open popup so label is immediately visible
    if (label) marker.togglePopup()
    this.markers.push(marker)
    return marker
  }

  /** Remove all markers from the map. */
  clearMarkers(): void {
    for (const m of this.markers) m.remove()
    this.markers = []
  }

  // --- 3D Terrain ---

  private terrainEnabled = false

  /**
   * AYNI (city close-up preview): raise the ceiling to the city zoom while
   * the camera's centre is over a city pack (and to the deep city zoom over
   * its zoom 16–17 photos, when the sphere corrections are in), lower it on leaving, limit the
   * tilt close over a pack, and keep the terrain's exaggeration in step
   * with the zoom.
   */
  private watchCityPack(map: MaplibreMap): void {
    const tilt = () => {
      if (this.flat) return
      const limit = cityMaxPitch(this.overCityPack, map.getZoom(), MAX_PITCH, this.sphereFixed)
      if (map.getMaxPitch() !== limit) {
        map.setMaxPitch(limit)
        dropStaleCameraCopy(map)
      }
    }
    const check = () => {
      const { lng, lat } = map.getCenter()
      const pack = cityPackAt(lng, lat)
      const over = pack !== null
      if (over !== this.overCityPack) {
        this.overCityPack = over
        setFineReason(map, 'cityPack', over)
      }
      const deep = this.sphereFixed && inDeepBox(pack, lng, lat)
      if (deep !== this.overDeepBox) {
        this.overDeepBox = deep
        setFineReason(map, 'cityDeep', deep)
      }
      if (pack && pack.id !== this.terrainPack) {
        this.terrainPack = pack.id
        if (this.terrainEnabled) this.applyTerrain()
      }
      tilt()
    }
    map.on('moveend', check)
    map.on('zoom', () => {
      tilt()
      // Crossing the terrain-off zoom mid-zoom: switch at once, so no frame
      // shows MapLibre's pillars. Heights follow the zoom on zoomend only:
      // every setTerrain rebuilds MapLibre's terrain.
      // With the sphere corrected (sphereCloseupFix.ts) the terrain stays on.
      const off = !this.sphereFixed && map.getZoom() >= CITY_TERRAIN_OFF_ZOOM
      if (this.terrainEnabled && off === (map.getTerrain() !== null)) this.applyTerrain()
      // The earth effects' sphere follows every zoom step (a uniform, cheap).
      else if (this.terrainEnabled) this.earthLayer?.setTerrainExaggeration(this.effectsLift(map.getTerrain()?.exaggeration ?? 0))
    })
    map.on('zoomend', () => {
      if (this.terrainEnabled) this.applyTerrain()
    })
    check()
  }

  /**
   * Draw the terrain as the switch and the zoom want it: off when switched
   * off, and in the city close-up preview also from {@link CITY_TERRAIN_OFF_ZOOM}
   * (`cityTerrainExaggeration` gives 0 there), unless MapLibre's sphere is corrected.
   */
  private applyTerrain(): void {
    const map = this.map
    if (!map || this.flat) return
    const exaggeration = !this.terrainEnabled ? 0
      : this.cityCloseup ? cityTerrainExaggeration(map.getZoom(), this.sphereFixed) : 5
    if (exaggeration > 0) {
      map.setTerrain({ source: this.cityCloseup ? cityTerrainSource(this.terrainPack) : 'terrain-dem', exaggeration })
    } else if (map.getTerrain()) {
      map.setTerrain(null as any)
    }
    this.earthLayer?.setTerrainExaggeration(this.effectsLift(exaggeration))
  }

  /**
   * The exaggeration the earth effects' sphere (night, glint, atmosphere) is
   * lifted for. It is lifted above Everest so exaggerated peaks on the globe's
   * edge stay under it; close over a city pack (from {@link CITY_TERRAIN_OFF_ZOOM})
   * the camera is only a few km up, inside that sphere, and the effects then
   * came from its far side through the planet, with a sharp brighter disc in
   * the middle where the far plane cuts it (seen 2026-10-18 with terrain kept
   * on). There it stays at sea level, as it was while terrain went off at 12.
   */
  private effectsLift(exaggeration: number): number {
    // With the dataset draped (dataDrape.ts) over the pack, the only ground with height, nothing needs lifting:
    // elsewhere the ground is at sea level (owner, 2026-10-18: the data floated further out).
    if (this.cityCloseup && this.drapeStop) return 0
    return this.cityCloseup && this.map && this.map.getZoom() >= CITY_TERRAIN_OFF_ZOOM ? 0 : exaggeration
  }

  /** Toggle 3D terrain elevation. Useful for topography/geology datasets. */
  toggleTerrain(enabled?: boolean): boolean {
    if (!this.map || !styleReady(this.map)) return false
    this.terrainEnabled = enabled ?? !this.terrainEnabled
    // City close-up preview: the pack's 5 m terrain (Catalonia; flat elsewhere), real heights,
    // and none from CITY_TERRAIN_OFF_ZOOM (applyTerrain).
    this.applyTerrain()
    return this.terrainEnabled
  }

  /**
   * AYNI: back to the default view, upright and untilted (double-click, and
   * the Reset button under auto-rotate since 2026-10-19). A spin carries on
   * from there: its next step waits for the flight to end.
   */
  resetView(): void {
    if (this.flat) {
      // The flat map's own default: the whole world.
      this.map?.flyTo({ center: [0, 0], zoom: this.flatFitZoom(), bearing: 0, pitch: 0, duration: 1200 })
      return
    }
    this.map?.flyTo({
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      bearing: 0,
      pitch: 0,
      duration: 2000,
    })
  }

  // --- AYNI: the flat 2:1 map (flatMap.ts, docs/AYNI_XR_FLAT_MAP_PLAN.md) ---

  private flat = false
  /** The layers' visibility as the viewer left it, kept while the flat map holds back the Mercator-only ones. */
  private flatHeldVisibility = new Map<string, string>()
  private flatCameraHook = false
  private flatSwitching: Promise<void> = Promise.resolve()

  /** AYNI (owner, 2026-10-19): whether this globe is shown as the flat 2:1 map. */
  isFlat(): boolean {
    return this.flat
  }

  /** The zoom at which the whole world fits the canvas, with a little room round it. */
  private flatFitZoom(): number {
    const canvas = this.map?.getCanvas()
    const w = canvas?.clientWidth || 1024
    const h = canvas?.clientHeight || 768
    // The world is 512·2^z px across (FLAT_EXTENT degrees) and half that high.
    return Math.log2(Math.min(w / 512, h / 256) * 0.94)
  }

  /**
   * AYNI (2026-10-19, plan F3): the place names the flat map shows for the
   * globe's label layers, whose Web Mercator tiles it cannot draw: Natural
   * Earth points (`scripts/build-flat-labels.ts`), in the same styles, with
   * fewer places further out. Shown while flat, as the Labels switch says.
   */
  private static readonly FLAT_LABELS: Record<string, { kind: string; base: string; rankByZoom: unknown[]; layout: Record<string, unknown>; paint: Record<string, unknown> }> = {
    'flat-country-labels': {
      kind: 'country', base: 'country-labels',
      rankByZoom: ['step', ['zoom'], 2, 2, 4, 3, 6, 4, 10],
      layout: { 'text-font': ['Open Sans Bold'], 'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 5, 16, 8, 20], 'text-transform': 'uppercase', 'text-letter-spacing': 0.1, 'text-max-width': 8 },
      paint: { 'text-color': 'rgba(255, 255, 255, 0.85)', 'text-halo-color': 'rgba(0, 0, 0, 0.7)', 'text-halo-width': 1.5 },
    },
    'flat-city-labels': {
      kind: 'city', base: 'city-labels',
      rankByZoom: ['step', ['zoom'], 0, 2.5, 1, 3.5, 2, 4.5, 3],
      layout: { 'text-font': ['Open Sans Regular'], 'text-size': ['interpolate', ['linear'], ['zoom'], 3, 9, 6, 13, 8, 16], 'text-max-width': 8 },
      paint: { 'text-color': 'rgba(220, 220, 255, 0.8)', 'text-halo-color': 'rgba(0, 0, 0, 0.6)', 'text-halo-width': 1 },
    },
    'flat-ocean-labels': {
      kind: 'ocean', base: 'ocean-labels',
      rankByZoom: ['step', ['zoom'], 1, 2.5, 2, 4, 3],
      layout: { 'text-font': ['Open Sans Italic'], 'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 5, 14], 'text-letter-spacing': 0.2, 'text-max-width': 10 },
      paint: { 'text-color': 'rgba(150, 200, 255, 0.6)', 'text-halo-color': 'rgba(0, 0, 0, 0.5)', 'text-halo-width': 1 },
    },
  }

  /** The flat labels on the map (once), each shown as its globe layer's choice says, and only while flat. */
  private syncFlatLabels(map: MaplibreMap): void {
    if (!map.getSource('flat-labels')) {
      map.addSource('flat-labels', { type: 'geojson', data: '/assets/flat-labels-ne-v1.json', attribution: 'Natural Earth' })
    }
    for (const [id, def] of Object.entries(MapRenderer.FLAT_LABELS)) {
      if (!map.getLayer(id)) {
        map.addLayer({
          id,
          type: 'symbol',
          source: 'flat-labels',
          filter: ['all', ['==', ['get', 'k'], def.kind], ['<=', ['get', 'r'], def.rankByZoom]] as unknown as maplibregl.FilterSpecification,
          layout: { ...def.layout, 'text-field': ['get', 'n'], 'symbol-sort-key': ['get', 'r'], visibility: 'none' } as maplibregl.SymbolLayerSpecification['layout'],
          paint: def.paint as maplibregl.SymbolLayerSpecification['paint'],
        })
      }
      const shown = this.flat && this.flatHeldVisibility.get(def.base) === 'visible'
      map.setLayoutProperty(id, 'visibility', shown ? 'visible' : 'none')
    }
  }

  /**
   * The layers drawn from Web Mercator vector tiles: the flat map holds them
   * back (the city photos are raster, and are cut to its grid instead).
   */
  private mercatorOnlyLayerIds(): string[] {
    return [...this.allOverlayLayerIds]
  }

  /**
   * AYNI (owner, 2026-10-19): show this globe as the flat 2:1 map, or the
   * globe again. The same map changes projection, so everything drawn with
   * MapLibre (pins, tracks, line, box, regions, vector and own layers,
   * markers, popups) and every camera call carry on; the earth layer draws
   * the dataset and overlays flat; the Earth's tiles become GIBS's
   * geographic ones. No turning or tilting on the flat map, no terrain,
   * stars or earth effects, and the spin waits.
   */
  setFlat(on: boolean): Promise<void> {
    this.flatSwitching = this.flatSwitching.then(() => this.applyFlat(on)).catch((err) => {
      logger.warn('[MapRenderer] flat map switch failed', err)
    })
    return this.flatSwitching
  }

  private async applyFlat(on: boolean): Promise<void> {
    const map = this.map
    if (!map || on === this.flat) return
    if (on) await registerFlatProjection()
    // The style itself, not every tile (`isStyleLoaded` waits for those too).
    while (!styleReady(map)) await new Promise<void>((resolve) => map.once('styledata', () => resolve()))
    const before = { center: map.getCenter(), zoom: map.getZoom() }
    const fitBefore = this.flat ? this.flatFitZoom() : 0
    if (on && this.autoRotating) {
      // The spin waits on the flat map; the button starts it again on the globe.
      this.autoRotating = false
      this.stopAutoRotate()
    }
    this.flat = on
    this.installFlatCameraHook(map)

    // The Earth's tiles: GIBS's geographic grid cut to the flat map's tiles, or Web Mercator.
    const tiles = (id: string, globe: string[], flatUrl: string) => {
      const source = map.getSource(id) as maplibregl.RasterTileSource | undefined
      source?.setTiles(on ? [flatUrl] : globe)
    }
    tiles('blue-marble', BLUE_MARBLE_TILES, FLAT_BLUE_MARBLE)
    tiles('black-marble', BLACK_MARBLE_TILES, FLAT_BLACK_MARBLE)
    // AYNI (owner, 2026-10-20): the city close-up's photos on the flat map too, cut from their
    // Web Mercator tiles to its grid, so it comes as close as the globe (zoom 16 over Barcelona and Rotterdam).
    if (this.cityCloseup) {
      for (const c of cityPhotoTiles()) tiles(c.source, [c.template], mercatorOnFlatUrl(c.template, c.minzoom, c.maxzoom))
    }

    // The Mercator-only layers: held back on the flat map, as the viewer left them on the globe.
    if (on) {
      this.flatHeldVisibility.clear()
      for (const id of this.mercatorOnlyLayerIds()) {
        if (!map.getLayer(id)) continue
        this.flatHeldVisibility.set(id, String(map.getLayoutProperty(id, 'visibility') ?? 'visible'))
        map.setLayoutProperty(id, 'visibility', 'none')
      }
      if (map.getTerrain()) map.setTerrain(null as never)
    }

    map.setProjection(on ? { type: FLAT_PROJECTION } : (this.cityCloseup ? ALWAYS_SPHERE_PROJECTION : { type: 'globe' }))
    this.earthLayer?.setFlat(on)
    if (on || map.getSource('flat-labels')) this.syncFlatLabels(map)

    // No turning or tilting on the flat map: north stays up.
    for (const handler of [map.dragRotate, map.touchPitch]) {
      if (on) handler.disable()
      else handler.enable()
    }
    if (on) {
      map.touchZoomRotate.disableRotation()
      map.keyboard.disableRotation()
      map.setMaxPitch(0)
      map.setMinZoom(Math.min(MIN_ZOOM, this.flatFitZoom() - 0.25))
      dropStaleCameraCopy(map)
    } else {
      map.touchZoomRotate.enableRotation()
      map.keyboard.enableRotation()
      map.setMinZoom(MIN_ZOOM)
      map.setMaxPitch(cityMaxPitch(this.overCityPack, map.getZoom(), MAX_PITCH, this.sphereFixed))
      dropStaleCameraCopy(map)
      for (const [id, visibility] of this.flatHeldVisibility) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility as 'visible' | 'none')
      }
      this.flatHeldVisibility.clear()
      this.setCityPhotosVisible(isEarthBody(this.baseLayerOptions?.celestialBody))
      this.applyTerrain()
    }

    // The camera: zoom means the globe's scale at the equator on both. The whole
    // globe in view becomes the whole world; closer in, the same place and scale.
    const lat = Math.max(-85, Math.min(85, before.center.lat))
    if (on) {
      const fit = this.flatFitZoom()
      map.jumpTo(before.zoom <= 3 ? { center: [0, 0], zoom: fit, bearing: 0, pitch: 0 } : { center: [before.center.lng, lat], zoom: before.zoom, bearing: 0, pitch: 0 })
    } else {
      map.jumpTo(before.zoom <= fitBefore + 0.35 ? { center: [before.center.lng, Math.max(-60, Math.min(60, lat))], zoom: DEFAULT_ZOOM } : { center: [before.center.lng, lat], zoom: before.zoom })
    }

    // GeoJSON is cut into tiles in the projection it was given in: give each source its data again.
    await this.recutGeoJson(map)
    map.triggerRepaint()
  }

  /** Every GeoJSON source is given its own data again, so its tiles follow the projection now in use. */
  private async recutGeoJson(map: MaplibreMap): Promise<void> {
    for (const id of Object.keys(map.getStyle()?.sources ?? {})) {
      const source = map.getSource(id) as maplibregl.GeoJSONSource | undefined
      if (source?.type !== 'geojson') continue
      try {
        source.setData(await source.getData())
      } catch {
        // a source removed meanwhile
      }
    }
  }

  /**
   * On the flat map the view centre stays on the Earth (MapLibre's planar
   * projections only keep it in the tile square, whose lower half is empty),
   * and a resize keeps the whole world reachable. Chained with any hook
   * already there (the city close-up keeps the camera above the ground).
   */
  private installFlatCameraHook(map: MaplibreMap): void {
    if (this.flatCameraHook) return
    this.flatCameraHook = true
    const prev = cameraUpdateHook(map)
    setCameraUpdateHook(map, (next) => {
      if (!this.flat) return prev ? prev(next) : {}
      const { lng, lat } = next.center
      const cl = Math.max(-180, Math.min(180, lng))
      const ca = Math.max(-90, Math.min(90, lat))
      return cl === lng && ca === lat ? {} : { center: new maplibregl.LngLat(cl, ca) }
    })
    map.on('resize', () => {
      if (!this.flat) return
      map.setMinZoom(Math.min(MIN_ZOOM, this.flatFitZoom() - 0.25))
      dropStaleCameraCopy(map)
    })
  }

  /** Toggle auto-rotation and return the new state. */
  toggleAutoRotate(): boolean {
    this.autoRotating = !this.autoRotating
    if (this.autoRotating) {
      this.startAutoRotate()
    } else {
      this.stopAutoRotate()
    }
    return this.autoRotating
  }

  /** AYNI: whether auto-rotation is on (a drag only pauses it). */
  isAutoRotating(): boolean {
    return this.autoRotating
  }

  /** AYNI: the spin as a multiple of the default speed; 0 while still. */
  getRotationRate(): number {
    return this.autoRotating ? this.rotationRate : 0
  }

  /**
   * Set the globe rotation rate. 0 = stop, 1.0 = default (30°/10s), 2.0 = double speed.
   * Starts rotation if rate > 0 and not already rotating; stops if rate is 0.
   * AYNI: up to 4, the top of the auto-rotate button's speed slider (was 2).
   * AYNI (2026-10-09): asking for what is already so does nothing. Stopping
   * stops the map (`map.stop()`), which would also cut short a camera move
   * under way, such as a Khipu step's, that has nothing to do with the spin.
   */
  setRotationRate(rate: number): void {
    const next = Math.max(0, Math.min(4, rate))
    // AYNI (2026-10-19): the flat map does not spin (flatMap.ts); the button waits for the globe.
    if (this.flat && next > 0) return
    if (next === 0 && !this.autoRotating) {
      this.rotationRate = 0
      return
    }
    if (next > 0 && this.autoRotating && next === this.rotationRate) return
    this.rotationRate = next
    if (this.rotationRate > 0) {
      if (!this.autoRotating) {
        this.autoRotating = true
        this.startAutoRotate()
      } else {
        // Restart with new rate
        this.startAutoRotate()
      }
    } else {
      this.autoRotating = false
      this.stopAutoRotate()
    }
  }

  private startAutoRotate(): void {
    this.stopAutoRotate()
    // Shift the center longitude to rotate around the polar axis (west-to-east).
    // This avoids the wobble caused by bearing rotation on a tilted globe.
    const degreesPerCycle = 30 * this.rotationRate
    const rotate = () => {
      if (!this.map || !this.autoRotating) return
      const center = this.map.getCenter()
      this.autoRotateStepEnds = performance.now() + 10000
      this.spinFrom = center.lng
      this.map.easeTo({
        center: [center.lng - degreesPerCycle, center.lat],
        duration: 10000,
        easing: (t: number) => t, // linear
      })
    }
    this.autoRotateStep = rotate
    rotate()
    // AYNI: each step starts when the last one ends, rather than on a
    // 10 s timer. A click (on the sphere or beside it) makes MapLibre stop
    // the step under way; on the timer the globe then stood still until
    // the next tick, so it read as switched off. Now it carries on.
    this.map?.on('moveend', this.continueAutoRotate)
    this.map?.on('move', this.followSpin)
    this.map?.on('movestart', this.spinInterrupted)
    // AYNI (owner, 2026-10-04): grabbing the globe only pauses the spin.
    // MapLibre stops the step under way when a drag, turn or tilt begins;
    // continueAutoRotate starts the next one once the globe stands still
    // again (`isMoving` covers the drag and its inertia). Auto-rotation
    // ends only when the viewer switches it off.
  }

  /**
   * AYNI: after a spin step ends. Run its course: the next step at once,
   * seamless. Cut short (a click, a scroll-zoom): the next step once the
   * map has stood still for a moment, so a zoom in progress is not fought.
   */
  private continueAutoRotate = (): void => {
    this.spinFrom = null
    if (!this.autoRotating || !this.autoRotateStep) return
    if (this.autoRotateResume !== null) clearTimeout(this.autoRotateResume)
    if (performance.now() >= this.autoRotateStepEnds - 50) {
      this.autoRotateStep()
      return
    }
    this.autoRotateResume = window.setTimeout(() => {
      this.autoRotateResume = null
      if (!this.autoRotating || !this.map) return
      if (this.map.isMoving()) this.continueAutoRotate()
      else this.autoRotateStep?.()
    }, 150)
  }

  /**
   * AYNI (owner, 2026-10-09): while a spin step runs, the longitude it moves
   * the camera through is added to the sky's turn, so the stars hold still.
   * A move the viewer starts (a drag, a zoom) ends the step first, and is not
   * counted: the stars follow the viewer's own moves, as they always have.
   */
  private followSpin = (e: { originalEvent?: unknown }): void => {
    if (this.spinFrom === null || !this.map || e.originalEvent) return
    const lng = this.map.getCenter().lng
    const delta = ((((lng - this.spinFrom) % 360) + 540) % 360) - 180
    this.spinFrom = lng
    this.skyTurn = (((this.skyTurn + delta) % 360) + 360) % 360
    this.earthLayer?.setSkyTurn(this.skyTurn)
  }

  /** A move the viewer starts ends the spin's share of the motion until its next step. */
  private spinInterrupted = (e: { originalEvent?: unknown }): void => {
    if (e.originalEvent) this.spinFrom = null
  }

  private stopAutoRotate(): void {
    this.spinFrom = null
    this.map?.off('move', this.followSpin)
    this.map?.off('movestart', this.spinInterrupted)
    if (this.autoRotateResume !== null) {
      clearTimeout(this.autoRotateResume)
      this.autoRotateResume = null
    }
    // Before map.stop() below, whose moveend would start the next step.
    this.map?.off('moveend', this.continueAutoRotate)
    this.autoRotateStep = null
    this.map?.stop() // cancel any in-flight easeTo
  }

  // --- Lat/lng tracking ---

  /**
   * Register callbacks for the cursor lat/lng display (and, for a
   * data-encoded dataset, the value readout that hangs off it).
   *
   * Three fixes over the original, all of which the data-encoded
   * readout made visible by giving the callback something more
   * consequential to say than a coordinate:
   *
   *   - Previous handlers are removed first. This used to stack a
   *     new pair on every call, so a second invocation drove the
   *     display twice per pointer event.
   *   - `touchmove` alongside `mousemove`, so a touch drag reports
   *     too. These are the events MapLibre actually fires; it has no
   *     `pointermove`/`pointerout`, and `Evented.on` accepts an
   *     unknown name without complaint, so registering those bound a
   *     handler that could never run. There is no touch equivalent of
   *     `mouseout` in play deliberately: a finger has no hover state,
   *     and clearing on lift would erase the reading at the moment it
   *     became readable.
   *   - Off-globe returns nothing. MapLibre's `e.lngLat` unprojects
   *     even where the sphere isn't, so the corners of the viewport
   *     used to report a coordinate for empty space.
   */
  setLatLngCallbacks(
    onUpdate: (lat: number, lng: number) => void,
    onClear: () => void
  ): void {
    const map = this.map
    if (!map) return
    this.clearLatLngCallbacks()

    const move = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (!this.isPointerOnGlobe(e)) {
        onClear()
        return
      }
      onUpdate(e.lngLat.lat, e.lngLat.lng)
    }
    const out = () => onClear()
    map.on('mousemove', move)
    map.on('touchmove', move)
    map.on('mouseout', out)
    this.latLngUnsubscribe = () => {
      map.off('mousemove', move)
      map.off('touchmove', move)
      map.off('mouseout', out)
    }
  }

  /**
   * Apply a palette / stretch / threshold transform to a data-encoded
   * dataset on this globe.
   *
   * A viewing decision, not a data one: the LUT the shader samples is
   * rebuilt and nothing else moves. `probeValueAt` keeps reporting the
   * same physical value under the same pixel, which is the invariant
   * `colorScaleDisplay` exists to protect — see its docstring.
   *
   * Buffered when the earth layer has not been created yet, the same
   * way `updateTexture` buffers, so a display chosen before the first
   * dataset finishes loading is not silently dropped.
   */
  setColorScaleDisplay(display: ColorScaleDisplay): void {
    this.colorScaleDisplay = display
    this.earthLayer?.setColorScaleDisplay(display)
  }

  /** The transform currently applied to this globe. */
  getColorScaleDisplay(): ColorScaleDisplay {
    return this.colorScaleDisplay
  }

  /**
   * AYNI: the scale the display transform recolours on this globe — the
   * dataset's own, a release's included — or null for a picture or before
   * the frame is up. The same object for as long as the dataset stays, so
   * a caller can tell a new dataset by identity (viewPaletteUI does).
   */
  displayScale(): ColorScale | null {
    return this.probeSource ? this.probeOptions?.colorScale ?? null : null
  }

  /**
   * Everything the statistics reducers need for the currently displayed
   * frame, or `null` when there is nothing to compute over.
   *
   * All-or-nothing on purpose: a snapshot without its scale is a grid of
   * meaningless bytes, and a scale without the bbox would put every
   * texel at the wrong latitude. Handing back one object means a caller
   * cannot pair a frame with the previous dataset's metadata.
   *
   * **Not for pointer handlers.** This reads the whole frame — see
   * `GlLumaSampler.snapshot`. Call it from an explicit user action.
   */
  analysisFrame(): {
    snapshot: LumaSnapshot
    scale: ColorScale
    options: DatasetOverlayOptions
  } | null {
    const options = this.probeOptions
    if (!this.probeSource || !options?.colorScale) return null
    const sampler = getSharedLumaSampler()
    if (!sampler) return null
    const snapshot = sampler.snapshot(this.probeSource)
    if (!snapshot) return null
    // AYNI: a value-encoded release has a calibration strip under its map,
    // so the tools get the map rectangle alone, with no crop left to apply.
    // Its scale carries the release, so every value is decoded exactly,
    // log and classified included (`lumaToValue`).
    if (options.releaseEncoding && !deliveredSizeMatches(options.releaseEncoding, snapshot.width, snapshot.height)) return null
    if (options.releaseEncoding && options.cropRect) {
      const cropped = this.croppedReleaseSnapshot(snapshot, options.cropRect)
      if (!cropped) return null
      return { snapshot: cropped, scale: options.colorScale, options: { ...options, cropRect: undefined } }
    }
    return { snapshot, scale: options.colorScale, options }
  }

  /** The last crop, kept so repeated statistics over a paused frame copy it once (the sampler caches the same way). */
  private releaseCrop: { from: LumaSnapshot; to: LumaSnapshot } | null = null

  private croppedReleaseSnapshot(
    snapshot: LumaSnapshot,
    crop: { u0: number; v0: number; us: number; vs: number },
  ): LumaSnapshot | null {
    if (this.releaseCrop?.from === snapshot) return this.releaseCrop.to
    const to = cropSnapshot(snapshot, crop)
    if (to) this.releaseCrop = { from: snapshot, to }
    return to
  }

  /**
   * Identity of the frame currently on the globe.
   *
   * The *same* expression `glLumaSampler` keys its snapshot cache on, so
   * "this string changed" and "the sampler would read a different frame"
   * are the same statement by construction rather than by coincidence.
   *
   * Deliberately not the `#time-label` text: that is snapped to the
   * dataset's display interval, so several video frames can share one
   * label, and it is hidden entirely for a dataset without start/end
   * times — a watch built on it goes silently inert exactly when it is
   * most needed. Costs a property read; no readback, no upload.
   */
  currentFrameId(): string | null {
    const source = this.probeSource
    if (!source) return null
    return source instanceof HTMLVideoElement ? `${source.currentTime}` : 'static'
  }

  // --- Analysed-region outline (Analyze §A3) ---

  private regionOutlineId: string | null = null

  /**
   * Outline the box the Analyze panel is measuring.
   *
   * Outline only, with no fill. A wash over the region would tint the
   * very values being measured, and the whole discipline of the
   * data-encoded path is that nothing decorative changes what a colour
   * means. The box says *where*; the globe still says *what*.
   */
  showRegionOutline(bounds: { n: number; s: number; w: number; e: number }): void {
    this.clearRegionOutline()
    if (!this.map) return
    const ring = boundsRing(bounds).map((p): [number, number] => [p.lon, p.lat])
    this.regionOutlineId = this.highlightRegion(
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [ring] },
      } as GeoJSON.Feature,
      { color: '#facc15', opacity: 0 },
    )
  }

  /**
   * AYNI (owner, 2026-10-18): outline a country Analyze is measuring, by its
   * own rings (Natural Earth, split at the antimeridian already), as lines
   * only: no fill, for the reason `showRegionOutline` gives.
   */
  showRegionShape(rings: { lat: number; lon: number }[][]): void {
    this.clearRegionOutline()
    if (!this.map) return
    this.regionOutlineId = this.highlightRegion(
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'MultiLineString', coordinates: rings.map((ring) => ring.map((p): [number, number] => [p.lon, p.lat])) },
      } as GeoJSON.Feature,
      { color: '#facc15', opacity: 0 },
    )
  }

  clearRegionOutline(): void {
    if (!this.regionOutlineId) return
    this.removeHighlight(this.regionOutlineId)
    this.regionOutlineId = null
  }

  // --- Isolines (Analyze §A5) ---

  private contourId: string | null = null

  /**
   * Draw the contour set the Analyze panel extracted.
   *
   * Its own source and layer rather than `highlightRegion`, for two
   * reasons. `highlightRegion` paints one fixed colour, and a contour map
   * needs each line drawn at its own level's colour — which is a
   * data-driven `['get', 'color']` over one FeatureCollection, not a
   * source per level. And it always adds a `fill` layer, which over a
   * MultiLineString draws nothing and is pure weight.
   *
   * Line only, no fill, for the same reason `showRegionOutline` refuses
   * one: a wash over the enclosed region would tint the values being
   * measured, and on this path nothing decorative is allowed to change
   * what a colour means. A dark halo underneath keeps a pale line legible
   * over a pale part of the ramp — without it the lightest levels vanish
   * into exactly the region they are describing.
   *
   * The geometry arrives already split at the antimeridian; see
   * `datasetContours.splitAtSeam` for why drawing it unsplit puts a
   * stripe across the globe.
   */
  showContours(levels: ContourLevel[]): void {
    this.clearContours()
    // AYNI: the style itself, not every tile: `isStyleLoaded` also waits for every source, and a globe streaming data (or the flat map's Earth) nearly always has one loading (as ownLayersOnMap.ts found).
    if (!this.map || !styleReady(this.map) || !levels.length) return
    const data = contourSetToGeoJson(levels)
    if (!data.features.length) return

    const id = `contours-${++this.highlightCounter}`
    const sourceId = `${id}-source`
    this.map.addSource(sourceId, { type: 'geojson', data })
    this.map.addLayer({
      id: `${id}-halo`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': 'rgba(0, 0, 0, 0.55)',
        'line-width': 3.5,
      },
    })
    this.map.addLayer({
      id: `${id}-line`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
      },
    })
    this.contourId = id
  }

  clearContours(): void {
    if (!this.contourId || !this.map) {
      this.contourId = null
      return
    }
    const id = this.contourId
    try { this.map.removeLayer(`${id}-line`) } catch { /* noop */ }
    try { this.map.removeLayer(`${id}-halo`) } catch { /* noop */ }
    try { this.map.removeSource(`${id}-source`) } catch { /* noop */ }
    this.contourId = null
  }

  // --- Transect picking (Analyze §A4) ---

  /** Vertices in the drawn line. Enough that the curve reads as smooth
   *  at any zoom without putting a meaningful number of points into a
   *  source that is redrawn on every drag frame. */
  private static readonly TRANSECT_LINE_VERTICES = 64

  private transectPoints: { lat: number; lon: number }[] = []
  private transectMarkers: maplibregl.Marker[] = []
  private transectLineId: string | null = null
  private transectUnsubscribe: (() => void) | null = null
  private transectOnChange: ((ends: TransectEndpoints | null) => void) | null = null
  /** AYNI (owner, 2026-10-18): a great circle, or along the first point's latitude. */
  private transectPath: LinePath = 'great-circle'

  /**
   * Arm two-point picking on the globe.
   *
   * `onChange` fires with the pair once the second point lands, again on
   * every drag of either endpoint, and with `null` when the transect is
   * cleared. Re-arming replaces any transect already on screen — one
   * line at a time, because two would need a legend to tell apart and
   * the panel only charts one.
   *
   * Dragging recomputes from a snapshot the caller already holds, so a
   * live drag costs a pure re-sample and no readback. That is why this
   * fires on `drag` rather than `dragend`.
   */
  beginTransect(onChange: (ends: TransectEndpoints | null) => void, path: LinePath = 'great-circle'): void {
    if (!this.map) return
    this.clearTransect()
    this.transectOnChange = onChange
    this.transectPath = path
    const map = this.map
    const onClick = (e: maplibregl.MapMouseEvent): void => {
      // AYNI: along a parallel, the second point keeps the first's latitude.
      const lat = path === 'parallel' && this.transectPoints.length === 1 ? this.transectPoints[0].lat : e.lngLat.lat
      this.transectPoints.push({ lat, lon: e.lngLat.lng })
      if (this.transectPoints.length === 1) {
        this.addTransectMarker(0)
        return
      }
      this.disarmTransectPicking()
      this.addTransectMarker(1)
      this.redrawTransectLine()
      this.emitTransect()
    }
    map.on('click', onClick)
    this.transectUnsubscribe = () => map.off('click', onClick)
  }

  /**
   * AYNI (the chatbot's `hovmoller.summary`, 2026-10-07): put a line on the
   * globe by its two ends, as if picked: draggable endpoints, the drawn
   * line, and `onChange` from then on (called once now with the ends).
   */
  setTransect(ends: TransectEndpoints, onChange: (ends: TransectEndpoints | null) => void): void {
    if (!this.map) return
    this.clearTransect()
    this.transectOnChange = onChange
    this.transectPath = ends.path ?? 'great-circle'
    this.transectPoints = [{ ...ends.from }, { ...ends.path === 'parallel' ? { lat: ends.from.lat, lon: ends.to.lon } : ends.to }]
    this.addTransectMarker(0)
    this.addTransectMarker(1)
    this.redrawTransectLine()
    this.emitTransect()
  }

  /** How many points of the pair have been placed. Drives the panel's
   *  "click two points" instruction. */
  transectProgress(): number {
    return this.transectPoints.length
  }

  /** Remove the line, its endpoints, and any pending pick. */
  clearTransect(): void {
    this.disarmTransectPicking()
    for (const m of this.transectMarkers) m.remove()
    this.transectMarkers = []
    this.transectPoints = []
    if (this.transectLineId) {
      this.removeHighlight(this.transectLineId)
      this.transectLineId = null
    }
    const notify = this.transectOnChange
    this.transectOnChange = null
    notify?.(null)
  }

  private disarmTransectPicking(): void {
    this.transectUnsubscribe?.()
    this.transectUnsubscribe = null
  }

  private addTransectMarker(index: number): void {
    if (!this.map) return
    const p = this.transectPoints[index]
    const marker = new maplibregl.Marker({ color: '#ffd166', draggable: true })
      .setLngLat([p.lon, p.lat])
      .addTo(this.map)
    marker.on('drag', () => {
      const at = marker.getLngLat()
      this.transectPoints[index] = { lat: at.lat, lon: at.lng }
      // AYNI: along a parallel both ends share the first's latitude: the start moves the line north or south, the end slides along it.
      if (this.transectPath === 'parallel' && this.transectPoints.length === 2) {
        const lat = this.transectPoints[0].lat
        const other = index === 0 ? 1 : 0
        if (index === 1) {
          this.transectPoints[1] = { lat, lon: at.lng }
          marker.setLngLat([at.lng, lat])
        } else {
          this.transectPoints[other] = { lat, lon: this.transectPoints[other].lon }
          this.transectMarkers[other]?.setLngLat([this.transectPoints[other].lon, lat])
        }
      }
      this.redrawTransectLine()
      this.emitTransect()
    })
    this.transectMarkers[index] = marker
  }

  private emitTransect(): void {
    if (this.transectPoints.length < 2) return
    this.transectOnChange?.({ from: this.transectPoints[0], to: this.transectPoints[1], ...(this.transectPath === 'parallel' ? { path: 'parallel' as const } : {}) })
  }

  // --- AYNI: the Data tab's box (Analyze over a drawn rectangle) ---

  private boxCorners: { lat: number; lon: number }[] = []
  private boxMarkers: maplibregl.Marker[] = []
  private boxUnsubscribe: (() => void) | null = null

  /**
   * Pick a box by two opposite corners: each click on the globe places
   * one, and both stay draggable. `onChange` gets the box when the second
   * lands and after every drag; the outline is the Analyze panel's region
   * outline, drawn once the box is its scope.
   */
  beginBox(onChange: (bounds: { n: number; s: number; w: number; e: number }) => void, onFirstCorner?: () => void): void {
    if (!this.map) return
    this.clearBox()
    const map = this.map
    const emit = () => {
      const [a, b] = this.boxCorners
      onChange({ n: Math.max(a.lat, b.lat), s: Math.min(a.lat, b.lat), w: Math.min(a.lon, b.lon), e: Math.max(a.lon, b.lon) })
    }
    const addCorner = (index: number) => {
      const p = this.boxCorners[index]
      const marker = new maplibregl.Marker({ color: '#facc15', draggable: true }).setLngLat([p.lon, p.lat]).addTo(map)
      marker.on('dragend', () => {
        const at = marker.getLngLat()
        this.boxCorners[index] = { lat: at.lat, lon: at.lng }
        if (this.boxCorners.length === 2) emit()
      })
      this.boxMarkers[index] = marker
    }
    const onClick = (e: maplibregl.MapMouseEvent): void => {
      if (!this.isPointerOnGlobe(e)) return
      this.boxCorners.push({ lat: e.lngLat.lat, lon: e.lngLat.lng })
      addCorner(this.boxCorners.length - 1)
      if (this.boxCorners.length === 1) {
        onFirstCorner?.()
        return
      }
      this.boxUnsubscribe?.()
      this.boxUnsubscribe = null
      map.getCanvas().classList.remove('data-pin-picking')
      emit()
    }
    map.on('click', onClick)
    map.getCanvas().classList.add('data-pin-picking')
    this.boxUnsubscribe = () => {
      map.off('click', onClick)
      map.getCanvas().classList.remove('data-pin-picking')
    }
  }

  /** How many of the box's two corners have been placed. */
  boxProgress(): number {
    return this.boxCorners.length
  }

  /** Remove the corners and any pending pick. Silent: nothing is called back. */
  clearBox(): void {
    this.boxUnsubscribe?.()
    this.boxUnsubscribe = null
    for (const m of this.boxMarkers) m.remove()
    this.boxMarkers = []
    this.boxCorners = []
  }

  // --- AYNI: Data tab pins (docs/AYNI_TOOLS_DATA_PLAN.md, Phase 3) ---

  private pinMarkers = new Map<string, maplibregl.Marker>()
  private pinPickUnsubscribe: (() => void) | null = null

  /**
   * What the probe can read on this globe: a value-encoded release, an
   * older data-encoded dataset, or nothing (a picture, or no frame yet).
   */
  probeKind(): 'release' | 'encoded' | null {
    if (!this.probeSource || !this.probeOptions?.colorScale) return null
    return this.probeOptions.releaseEncoding ? 'release' : 'encoded'
  }

  /**
   * The whole frame of a value-encoded release with its options, for "Mark
   * highest & lowest"; `analysisFrame` stays null for releases. A press,
   * never a pointer handler (see `GlLumaSampler.snapshot`).
   */
  releaseFrame(): { snapshot: LumaSnapshot; options: DatasetOverlayOptions } | null {
    const options = this.probeOptions
    if (!this.probeSource || !options?.releaseEncoding) return null
    const snapshot = getSharedLumaSampler()?.snapshot(this.probeSource)
    return snapshot ? { snapshot, options } : null
  }

  /**
   * AYNI: the codes under each point of a line on the frame on the globe
   * (the Hovmöller diagram, `research/hovmoller.ts`), with the scale they
   * decode by. One draw on the graphics card per frame; the whole-frame
   * readback (`analysisFrame`) only if the card refuses. The texels are
   * the ones the Line's profile reads (`texelsAlong`). -1 outside the
   * dataset. Null with nothing to read.
   */
  lineCodesAt(points: readonly { lat: number; lon: number }[]): { codes: Int16Array; scale: ColorScale } | null {
    const options = this.probeOptions
    const source = this.probeSource
    if (!source || !options?.colorScale || points.length === 0) return null
    const sampler = getSharedLumaSampler()
    if (!sampler) return null
    const { width, height } = probeSourceSize(source)
    if (!width || !height) return null
    const texels = texelsAlong(points, width, height, options)
    const read = sampler.sampleTexels?.(source, texels) ?? null
    if (read) {
      const codes = new Int16Array(points.length)
      for (let i = 0; i < codes.length; i++) codes[i] = texels[i * 2] < 0 ? OUTSIDE_CODE : read[i]
      return { codes, scale: options.colorScale }
    }
    const snapshot = sampler.snapshot(source)
    if (!snapshot) return null
    return { codes: codesAtTexels(snapshot.data, snapshot.width, texels), scale: options.colorScale }
  }

  /**
   * Show these pins as coloured dots, each draggable; `onMove` fires when a
   * drag ends. Markers are kept by id, so a re-render does not interrupt a
   * drag in progress.
   *
   * AYNI (owner, 2026-10-09): `value`, when given, is drawn beside the dot
   * as the headset draws it (vrPins.ts): at the end of a short stem, up and
   * to the right for a pin in the northern hemisphere, down and to the left
   * for one in the southern, so it never sits over the data at the pin. It
   * hides while the pin is round the back of the globe, where MapLibre
   * only dims the dot.
   *
   * AYNI (owner, 2026-10-09): a pin the viewer named (`named`) shows its
   * name in front of the value; `scrubbed` marks a value read off the pin
   * graph under the pointer rather than the frame on the globe (dashed).
   */
  setPins(
    pins: ReadonlyArray<{ id: string; lat: number; lon: number; color: string; label: string; value?: string; named?: boolean; scrubbed?: boolean }>,
    onMove: (id: string, lat: number, lon: number) => void,
  ): void {
    const keep = new Set(pins.map((p) => p.id))
    for (const [id, marker] of this.pinMarkers) {
      if (keep.has(id)) continue
      marker.remove()
      this.pinMarkers.delete(id)
    }
    if (!this.map) return
    for (const pin of pins) {
      let marker = this.pinMarkers.get(pin.id)
      if (!marker) {
        const el = document.createElement('div')
        el.className = 'data-pin-marker'
        el.style.setProperty('--pin-color', pin.color)
        const stem = document.createElement('span')
        stem.className = 'data-pin-stem'
        const value = document.createElement('span')
        value.className = 'data-pin-value'
        // The marker's own name carries the value for a screen reader.
        stem.setAttribute('aria-hidden', 'true')
        value.setAttribute('aria-hidden', 'true')
        el.append(stem, value)
        // Placed before it is added: MapLibre positions a marker as it adds it.
        marker = new maplibregl.Marker({ element: el, draggable: true }).setLngLat([pin.lon, pin.lat]).addTo(this.map)
        const m = marker
        marker.on('dragend', () => {
          const at = m.getLngLat()
          onMove(pin.id, at.lat, at.lng)
        })
        this.pinMarkers.set(pin.id, marker)
      }
      marker.setLngLat([pin.lon, pin.lat])
      const el = marker.getElement()
      const value = pin.value ?? ''
      const text = pinMarkLabel(pin.named ? pin.label : null, value)
      const valueEl = el.querySelector<HTMLElement>('.data-pin-value')
      if (valueEl && valueEl.textContent !== text) valueEl.textContent = text
      el.classList.toggle('has-value', text !== '')
      el.classList.toggle('scrubbed', !!pin.scrubbed)
      el.classList.toggle('south', pin.lat < 0)
      const name = value ? `${pin.label}: ${value}` : pin.label
      el.setAttribute('aria-label', name)
      el.title = name
    }
    this.watchPinCover()
  }

  /** AYNI (owner, 2026-10-18): the tracks last asked for, kept to draw once the style is ready. */
  private pinTracks: ReadonlyArray<{ id: string; color: string; segments: ReadonlyArray<ReadonlyArray<{ lat: number; lon: number }>> }> = []
  private pinTracksWaiting = false
  private static readonly PIN_TRACKS_SOURCE = 'ayni-pin-tracks'
  private static readonly PIN_TRACKS_CASING = 'ayni-pin-tracks-casing'
  private static readonly PIN_TRACKS_LINE = 'ayni-pin-tracks-line'

  /**
   * AYNI (owner, 2026-10-18): the track a pin following the highest or
   * lowest value left (`measure/followExtremes.ts`), in the pin's colour
   * over a dark casing so it reads on any data, each step along the great
   * circle (`densifyTrack`). Each track is a set of
   * segments; a longitude past ±180 is drawn where it belongs. Empty: none.
   * The layers go on top of the dataset and its overlays; the pins
   * themselves (markers) stay above them.
   */
  setPinTracks(tracks: ReadonlyArray<{ id: string; color: string; segments: ReadonlyArray<ReadonlyArray<{ lat: number; lon: number }>> }>): void {
    this.pinTracks = tracks
    const map = this.map
    if (!map) return
    if (!styleReady(map)) {
      if (!this.pinTracksWaiting) {
        this.pinTracksWaiting = true
        map.once('styledata', () => {
          this.pinTracksWaiting = false
          this.setPinTracks(this.pinTracks)
        })
      }
      return
    }
    const data: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: tracks
        .filter((tr) => tr.segments.length)
        .map((tr) => ({
          type: 'Feature',
          properties: { id: tr.id, color: tr.color },
          // Along the great circle, as the pin went (straight in lat/lon bends near a pole).
          geometry: { type: 'MultiLineString', coordinates: densifyTrack(tr.segments).map((seg) => seg.map((p) => [p.lon, p.lat])) },
        })),
    }
    const source = map.getSource(MapRenderer.PIN_TRACKS_SOURCE) as maplibregl.GeoJSONSource | undefined
    if (!source) {
      if (!data.features.length) return
      map.addSource(MapRenderer.PIN_TRACKS_SOURCE, { type: 'geojson', data })
      map.addLayer({
        id: MapRenderer.PIN_TRACKS_CASING,
        type: 'line',
        source: MapRenderer.PIN_TRACKS_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': 'rgba(0, 0, 0, 0.55)', 'line-width': 5 },
      })
      map.addLayer({
        id: MapRenderer.PIN_TRACKS_LINE,
        type: 'line',
        source: MapRenderer.PIN_TRACKS_SOURCE,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': 2.5 },
      })
      return
    }
    source.setData(data)
    // Overlays added since go under the track.
    const layers = map.getStyle()?.layers ?? []
    if (layers[layers.length - 1]?.id !== MapRenderer.PIN_TRACKS_LINE) {
      try {
        map.moveLayer(MapRenderer.PIN_TRACKS_CASING)
        map.moveLayer(MapRenderer.PIN_TRACKS_LINE)
      } catch { /* the layers may be gone with the map */ }
    }
  }

  private pinCoverUnsubscribe: (() => void) | null = null

  /** While pins are down, mark each one round the back of the globe, on every frame drawn. */
  private watchPinCover(): void {
    const map = this.map
    if (!map || this.pinMarkers.size === 0) {
      this.pinCoverUnsubscribe?.()
      this.pinCoverUnsubscribe = null
      return
    }
    const check = (): void => {
      for (const marker of this.pinMarkers.values()) {
        marker.getElement().classList.toggle('covered', isOccluded(map, marker.getLngLat()))
      }
    }
    if (!this.pinCoverUnsubscribe) {
      map.on('render', check)
      this.pinCoverUnsubscribe = () => map.off('render', check)
    }
    check()
  }

  /**
   * Pin mode: each click on the globe calls `onPick` until disarmed. Clicks
   * off the sphere are ignored. The canvas shows a crosshair meanwhile.
   */
  armPinPick(onPick: (lat: number, lon: number) => void): void {
    if (!this.map) return
    this.disarmPinPick()
    const map = this.map
    const onClick = (e: maplibregl.MapMouseEvent): void => {
      if (!this.isPointerOnGlobe(e)) return
      onPick(e.lngLat.lat, e.lngLat.lng)
    }
    map.on('click', onClick)
    map.getCanvas().classList.add('data-pin-picking')
    this.pinPickUnsubscribe = () => {
      map.off('click', onClick)
      map.getCanvas().classList.remove('data-pin-picking')
    }
  }

  disarmPinPick(): void {
    this.pinPickUnsubscribe?.()
    this.pinPickUnsubscribe = null
  }

  /**
   * Draw the line the samples are taken along.
   *
   * Densified rather than drawn as a two-point LineString: MapLibre
   * renders a segment as a straight line in projected space, which on a
   * globe is not the great circle the samples follow. A viewer
   * comparing the chart against the line has to be looking at the same
   * path, so the geometry is subdivided along the same spherical
   * interpolation `sampleTransect` uses.
   */
  private redrawTransectLine(): void {
    if (!this.map || this.transectPoints.length < 2) return
    if (this.transectLineId) {
      this.removeHighlight(this.transectLineId)
      this.transectLineId = null
    }
    const [from, to] = this.transectPoints
    const ends: TransectEndpoints = { from, to, ...(this.transectPath === 'parallel' ? { path: 'parallel' as const } : {}) }
    // AYNI: a parallel the whole way round needs more vertices than a short great circle to look round.
    const vertices = this.transectPath === 'parallel' ? Math.max(MapRenderer.TRANSECT_LINE_VERTICES, Math.ceil(parallelSpanDeg(ends) / 2)) : MapRenderer.TRANSECT_LINE_VERTICES
    const coords = linePath(ends, vertices, true)
      .map((p): [number, number] => [p.lon, p.lat])
    this.transectLineId = this.highlightRegion(
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: coords },
      } as GeoJSON.Feature,
      { color: '#ffd166' },
    )
  }

  /** The geographic box currently in view, for "analyse what I can
   *  see". Null when the map is not up yet. */
  visibleBounds(): { n: number; s: number; w: number; e: number } | null {
    if (!this.map) return null
    const b = this.map.getBounds()
    return { n: b.getNorth(), s: b.getSouth(), w: b.getWest(), e: b.getEast() }
  }

  /**
   * Read the dataset value under a geographic point, or `null` when
   * there is nothing meaningful to report — a picture dataset, a
   * point outside a regional dataset's box, or no decoded frame yet.
   *
   * Deliberately independent of `setColorScaleDisplay`: recolouring the
   * globe must never move this number.
   */
  probeValueAt(lat: number, lon: number): ProbeReading | null {
    if (!this.probeSource || !this.probeOptions?.colorScale) return null
    // Shared across every renderer on the page: one WebGL2 context
    // total rather than one per panel. `null` means no WebGL2, in
    // which case there is no globe either, so no readout is right.
    const sampler = getSharedLumaSampler()
    if (!sampler) return null
    return probeDatasetValue(
      lat, lon, this.probeSource, (s, uv) => sampler.sample(s, uv), this.probeOptions)
  }

  /** Detach the lat/lng handlers registered by `setLatLngCallbacks`. */
  clearLatLngCallbacks(): void {
    this.latLngUnsubscribe?.()
    this.latLngUnsubscribe = null
  }

  /**
   * Whether the pointer is actually over the globe.
   *
   * On a globe projection MapLibre still unprojects points in the
   * empty space around the sphere, so `e.lngLat` alone is not a test.
   * Re-projecting the returned coordinate and comparing against the
   * original screen point is: for a point off the sphere the round
   * trip does not come back where it started.
   */
  private isPointerOnGlobe(e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): boolean {
    const map = this.map
    if (!map) return false
    try {
      const back = map.project(e.lngLat)
      const dx = back.x - e.point.x
      const dy = back.y - e.point.y
      return dx * dx + dy * dy < ON_GLOBE_TOLERANCE_PX * ON_GLOBE_TOLERANCE_PX
    } catch {
      return false
    }
  }

  // --- Custom layers (for Phase 1+) ---

  /** Add a custom layer (e.g. day/night blend, clouds). */
  addCustomLayer(layer: CustomLayerInterface, beforeId?: string): void {
    this.map?.addLayer(layer as unknown as maplibregl.LayerSpecification, beforeId)
  }

  // --- Canvas description ---

  /** Update the canvas ARIA label. */
  setCanvasDescription(text: string): void {
    this.map?.getCanvas().setAttribute('aria-label', text)
  }

  // --- Dataset overlays (Spike B) ---

  /**
   * Display an equirectangular image on the globe via custom layer sphere.
   * Uses proper equirectangular UV mapping — no Mercator distortion, full
   * pole coverage.
   */
  /**
   * Phase 3e: hide vs keep the base raster layers based on the
   * dataset's overlay options.
   *
   *   Earth + no bbox       hide (the dataset's full-equirectangular
   *                              projection covers every pixel)
   *   Earth + bbox          show (shader discards outside the box;
   *                              base tiles fill the rest of the globe)
   *   Earth + transparent   show (AYNI, owner 2026-10-12: an alpha
   *                              stream or a palette's clear codes show
   *                              the GIBS tiles, the only basemap)
   *   non-Earth (any case)  hide (Earth's blue/black marble are the
   *                              wrong textures for Mars / Moon / Sun /
   *                              etc.; we render the dataset on a
   *                              clean sphere until 3f bundles
   *                              per-body surface textures)
   *
   * Returns void; callers use it as a single source of truth for
   * "what should the base look like behind this dataset?". */
  /** AYNI Studio: the last dataset options the base tiles were set for, and whether a data move uncovers them. */
  private baseLayerOptions: DatasetOverlayOptions | undefined
  private dataMoveUncovers = false
  private datasetFade: { opacity: number; wipe: LayerReveal['wipe'] } = { opacity: 1, wipe: null }
  /** AYNI (owner, 2026-10-12): the viewer's own opacity for the dataset (its transparency; 0 while its eye hides it). */
  private datasetUserOpacity = 1
  /** AYNI (DSA 1.8): the dataset's video is drawn by a vector layer instead. */
  private datasetSuperseded = false
  /** AYNI (owner, 2026-10-23): the rings of a ringed planet around this globe (planetRingsMapLayer.ts). */
  private planetRings: PlanetRingsMapLayer | null = null
  /** The ringed body the dataset names (lower case), or null. */
  private ringBody: string | null = null

  /**
   * AYNI (owner, 2026-10-23): Saturn, Jupiter, Uranus and Neptune get their
   * rings around the globe (planetRings.ts), in the body's equatorial plane,
   * above every other layer. The layer is added on the first ringed body
   * and then only told which body (or none); the flat map draws none.
   */
  private syncPlanetRings(): void {
    const map = this.map
    if (!map || !this.globeLayersBuilt) return
    if (!this.ringBody) {
      this.planetRings?.setBody(null)
      return
    }
    if (!this.planetRings) this.planetRings = createPlanetRingsMapLayer({ isFlat: () => this.flat })
    if (!map.getLayer(PLANET_RINGS_MAP_LAYER_ID)) {
      try {
        map.addLayer(this.planetRings as unknown as maplibregl.LayerSpecification)
      } catch (err) {
        logger.warn('[MapRenderer] planet rings layer not added:', err)
        return
      }
    }
    this.planetRings.setBody(this.ringBody)
  }

  /** The body whose rings this globe draws, lower case, or null (tests and checks). */
  ringedBody(): string | null {
    return this.ringBody
  }

  private applyBaseLayerVisibility(options: DatasetOverlayOptions | undefined): void {
    this.baseLayerOptions = options
    const earth = isEarthBody(options?.celestialBody)
    // AYNI (owner, 2026-10-23): the rings of a ringed planet, by the body the dataset names.
    const body = offEarthBody({ celestialBody: options?.celestialBody })
    this.ringBody = ringSystemOf(body)?.body ?? null
    this.syncPlanetRings()
    // An opaque whole-globe dataset hides the Earth's tiles; a see-through one,
    // or a data move fading or wiping it, shows them.
    const transparent = Boolean(options?.hasAlphaStream || options?.colorScale || options?.releaseEncoding)
    const showBase = earth && (Boolean(options?.boundingBox) || transparent || this.dataMoveUncovers)
    const visibility = showBase ? 'visible' : 'none'
    try { this.map?.setLayoutProperty('blue-marble-layer', 'visibility', visibility) } catch { /* noop */ }
    try { this.map?.setLayoutProperty('black-marble-layer', 'visibility', visibility) } catch { /* noop */ }
    // AYNI (owner, 2026-10-14): the city close-up's photos are Earth tiles
    // too; under another body they never show (offEarthBody.ts).
    this.setCityPhotosVisible(earth)
  }

  /** AYNI: show or hide the city close-up's photo layers (only in the preview, which has them). */
  private setCityPhotosVisible(visible: boolean): void {
    if (!this.cityCloseup) return
    for (const pack of CITY_PACKS) {
      for (const id of [`${cityPhotoSource(pack.id)}-low-layer`, `${cityPhotoSource(pack.id)}-layer`, `${cityPhotoSource(pack.id)}-deep-layer`]) {
        try { this.map?.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none') } catch { /* noop */ }
      }
    }
  }

  /**
   * AYNI: the basemap under the dataset and the overlays above it (see
   * EarthTileLayerControl.setMapLayers). Buffered until the earth layer
   * exists, like the dataset itself.
   */
  setMapLayers(layers: MapLayerImages | null): void {
    if (!this.earthLayer) {
      this.pendingMapLayers = layers
      return
    }
    this.earthLayer.setMapLayers(layers)
  }

  /** AYNI Studio: upload the dataset video's current frame again (a render's every frame; see renderMode.ts `reuploadFrame`). */
  requestVideoUpdate(): void {
    this.earthLayer?.requestVideoUpdate()
  }

  /** AYNI (multi-globe plan, phase 5): the dataset with its layers as a 2:1 equirectangular picture (rows bottom first), or null. */
  unwrapFrame(width: number, height: number): Promise<Uint8Array | null> {
    return this.earthLayer?.requestUnwrap(width, height) ?? Promise.resolve(null)
  }

  /** AYNI Studio: fade the dataset towards the basemap under it (a data move); 1 = as drawn. */
  setDatasetOpacity(opacity: number): void {
    this.datasetFade.opacity = opacity
    this.earthLayer?.setDatasetOpacity(this.datasetSuperseded ? 0 : opacity)
    this.syncDataMoveBase()
  }

  /**
   * AYNI (DSA 1.8): a click on the globe picks a place now (a pin, a
   * transect's ends), so it must not also open a vector feature's panel.
   */
  isPickingPlaces(): boolean {
    return this.transectUnsubscribe !== null || !!this.map?.getCanvas().classList.contains('data-pin-picking')
  }

  /**
   * AYNI (DSA 1.8): a vector layer that supersedes the dataset's video
   * (`supersedesLayers`) hides it here, apart from Studio's fade, which
   * keeps its own value. The video still plays and decodes underneath:
   * pins, probes and the vector clock read it.
   */
  setDatasetSuperseded(hidden: boolean): void {
    if (hidden === this.datasetSuperseded) return
    this.datasetSuperseded = hidden
    this.earthLayer?.setDatasetOpacity(hidden ? 0 : this.datasetFade.opacity)
    this.syncDataMoveBase()
  }

  /**
   * AYNI (owner, 2026-10-12): the viewer's own opacity for the dataset, 1
   * opaque … 0 gone (its eye). It multiplies a data move's fade, and the
   * Earth's tiles show under it while it is below 1.
   */
  setDatasetUserOpacity(opacity: number): void {
    this.datasetUserOpacity = Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1
    this.earthLayer?.setDatasetUserOpacity(this.datasetUserOpacity)
    this.syncDataMoveBase()
  }

  /** AYNI Studio: clear the dataset behind a wipe's edge, where the next one has arrived (null: none). */
  setDatasetWipe(wipe: LayerReveal['wipe']): void {
    this.datasetFade.wipe = wipe
    this.earthLayer?.setDatasetWipe(wipe)
    this.syncDataMoveBase()
  }

  /** The Earth's tiles under a dataset that a data move fades or wipes, and hidden again after. */
  private syncDataMoveBase(): void {
    const uncovers = this.datasetFade.opacity < 1 || this.datasetFade.wipe !== null || this.datasetSuperseded || this.datasetUserOpacity < 1
    if (uncovers === this.dataMoveUncovers) return
    this.dataMoveUncovers = uncovers
    this.applyBaseLayerVisibility(this.baseLayerOptions)
  }

  /**
   * AYNI: the frame this globe draws its dataset from and where its map
   * sits in it, for drawing it against another globe (Compare, research
   * tier); null without a value-encoded dataset.
   */
  liveSource(): { source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement; crop: { u0: number; v0: number; us: number; vs: number } | null } | null {
    if (!this.probeSource || !this.probeOptions?.colorScale) return null
    return { source: this.probeSource, crop: this.probeOptions.cropRect ?? null }
  }

  /**
   * AYNI: draw the dataset as its change from a held baseline, every frame,
   * on the graphics card (research tier; `EarthTileLayerControl.setChangeView`).
   * False when this globe cannot, so the caller falls back to drawing the
   * change when playback stops.
   */
  setChangeView(view: Parameters<EarthTileLayerControl['setChangeView']>[0]): boolean {
    if (!this.earthLayer) return view === null
    return this.earthLayer.setChangeView(view)
  }

  /** AYNI: the climatology layer (calendar month) last drawn and the frame time it was chosen for; null when none (checks read it). */
  climLayerShown(): { layer: number; mediaTime: number | null } | null {
    return this.earthLayer?.climLayerShown() ?? null
  }

  /**
   * AYNI (CR-D3, "zoom by data resolution"): let the camera come to 9.5
   * while this globe shows a dataset whose frame pixel is finer than about
   * 200 m (Landsat over a city), and keep it at 8 for coarser grids, which
   * would only zoom into blurry squares. A video's size is known once its
   * metadata loads, so it is read again then. For one dataset the ceiling
   * only rises: an adaptive stream dropping to a smaller rendition must not
   * pull the camera out.
   */
  private applyDatasetZoom(source: ProbeSource | null, options: DatasetOverlayOptions | null): void {
    const old = this.zoomSource
    if (old instanceof HTMLVideoElement && this.zoomSourceListener) {
      old.removeEventListener('loadedmetadata', this.zoomSourceListener)
      old.removeEventListener('resize', this.zoomSourceListener)
    }
    this.zoomSource = source
    this.zoomSourceListener = null
    const map = this.map
    if (!map) return
    if (!source) {
      setFineReason(map, 'dataset', false)
      return
    }
    let fine = false
    const check = () => {
      fine = fine || isFineGrid(probeSourceSize(source), options)
      setFineReason(map, 'dataset', fine)
    }
    check()
    if (source instanceof HTMLVideoElement && !fine) {
      this.zoomSourceListener = check
      source.addEventListener('loadedmetadata', check)
      source.addEventListener('resize', check)
    }
  }

  updateTexture(
    texture: HTMLCanvasElement | HTMLImageElement,
    options?: DatasetOverlayOptions,
  ): void {
    if (!this.earthLayer) {
      // Buffer until the earth layer is created on map 'load'
      this.pendingTexture = texture
      this.pendingVideo = null
      this.pendingDatasetOptions = options ?? null
      return
    }
    this.earthLayer.setDatasetTexture(texture, options)
    this.probeSource = texture
    this.probeOptions = options ?? null
    this.applyDatasetZoom(texture, options ?? null)
    this.applyBaseLayerVisibility(options)
    logger.info('[MapRenderer] Dataset overlay set via custom layer sphere')
  }

  /**
   * Display an equirectangular video on the globe via custom layer sphere.
   * The render loop updates the texture from the video element each frame.
   * Returns a lightweight handle for playback controller compatibility.
   */
  setVideoTexture(video: HTMLVideoElement, options?: DatasetOverlayOptions): VideoTextureHandle {
    if (this.earthLayer) {
      this.earthLayer.setDatasetVideo(video, options)
      this.probeSource = video
      this.probeOptions = options ?? null
      this.applyDatasetZoom(video, options ?? null)
      this.applyBaseLayerVisibility(options)
      logger.info('[MapRenderer] Video dataset set via custom layer sphere')
    } else {
      // Buffer until the earth layer is created on map 'load'
      this.pendingVideo = video
      this.pendingTexture = null
      this.pendingDatasetOptions = options ?? null
    }
    const earthLayer = this.earthLayer
    let pending = false
    return {
      get needsUpdate() { return pending },
      set needsUpdate(v: boolean) {
        pending = v
        if (v) { earthLayer?.requestVideoUpdate(); pending = false }
      },
      dispose() {},
    }
  }

  /**
   * Playhead of the frame currently in this panel's dataset texture, or
   * `null` when none has been uploaded.
   *
   * What the globe is *showing*, as opposed to what its video element
   * reports. The two diverge whenever an upload is skipped, which is
   * precisely the case a sibling-alignment check has to catch.
   */
  getUploadedFrameTime(): number | null {
    return this.earthLayer?.getUploadedFrameTime() ?? null
  }

  /** AYNI Studio: whether the stars have loaded (they load when the browser is idle). */
  isSkyboxReady(): boolean {
    return this.earthLayer?.isSkyboxReady() ?? false
  }

  // --- Earth materials ---

  /** Wait for the earth tile layer's textures (night lights, specular, clouds) to load. */
  async loadDefaultEarthMaterials(onProgress?: (fraction: number) => void): Promise<void> {
    // Wait for earth layer to be created (it's added on map 'load')
    if (!this.earthLayer) {
      logger.debug('[MapRenderer] loadDefaultEarthMaterials: waiting for earth layer...')
      // Generous timeout: in multi-viewport mode four maps compete for
      // bandwidth and the primary's `load` event can take >10s on
      // mobile Safari. 30s keeps the poll bounded without being
      // fatal for slow connections.
      const EARTH_LAYER_TIMEOUT_MS = 30_000
      await new Promise<void>((resolve, reject) => {
        const start = Date.now()
        const check = () => {
          if (this.earthLayer) return resolve()
          if (!this.map || Date.now() - start > EARTH_LAYER_TIMEOUT_MS) {
            return reject(new Error('[MapRenderer] Timed out waiting for earth layer'))
          }
          setTimeout(check, 50)
        }
        check()
      })
    }
    logger.debug('[MapRenderer] loadDefaultEarthMaterials: earth layer found, waiting for textures...')
    onProgress?.(0.2)
    await this.earthLayer!.ready
    logger.debug('[MapRenderer] loadDefaultEarthMaterials: textures ready')
    onProgress?.(1)
  }

  /** Hide earth effects (day/night, city lights, specular, clouds) when a dataset is active. */
  removeNightLights(): void {
    this.effectsHidden = true
    this.earthLayer?.setVisible(false)
    // Also hide the atmosphere glow since it conflicts with dataset overlays
    try { this.map?.setSky({ 'atmosphere-blend': 0 }) } catch { /* noop */ }
  }

  /** Update sun direction and re-show the earth tile layer + atmosphere. */
  enableSunLighting(lat: number, lng: number): void {
    this.effectsHidden = false
    this.earthLayer?.clearDatasetTexture()
    this.probeSource = null
    this.probeOptions = null
    this.applyDatasetZoom(null, null)
    this.earthLayer?.setVisible(true)
    this.earthLayer?.setSunPosition(lat, lng)
    // Restore tile bases (may have been hidden for dataset overlay)
    try { this.map?.setLayoutProperty('blue-marble-layer', 'visibility', 'visible') } catch { /* noop */ }
    try { this.map?.setLayoutProperty('black-marble-layer', 'visibility', 'visible') } catch { /* noop */ }
    this.setCityPhotosVisible(true)
    // Restore atmosphere glow (may have been hidden for dataset overlay)
    try {
      this.map?.setSky({
        'atmosphere-blend': [
          'interpolate', ['linear'], ['zoom'],
          0, 1, 5, 1, 7, 0,
        ] as any,
      })
    } catch { /* noop */ }
  }

  /** Clear sun override — reverts to real-time sun position.
   *  Does NOT re-show the earth layer; enableSunLighting() handles that. */
  disableSunLighting(): void {
    this.earthLayer?.clearSunOverride()
  }

  /** Clouds are loaded by the earth tile layer automatically. Report complete. */
  async loadCloudOverlay(_url: string, _onProgress?: (fraction: number) => void): Promise<void> {
    _onProgress?.(1)
  }

  /** Cloud removal is handled by the earth tile layer. */
  removeCloudOverlay(): void {
    // Phase 1
  }

  // --- View context for LLM ---

  /**
   * Query the current viewport state and visible geographic features.
   * Returns a structured object the LLM can use for richer context.
   */
  /** Extract unique feature names from a source layer, filtered to the viewport bounds.
   *  Only considers Point features to avoid false positives from off-screen geometry. */
  private queryNamesInBounds(sourceLayer: string, bounds: maplibregl.LngLatBounds, classFilter?: string): string[] {
    if (!this.map) return []
    const names: string[] = []
    const seen = new Set<string>()
    try {
      const features = this.map.querySourceFeatures('openmaptiles', { sourceLayer })
      for (const f of features) {
        // Only process Point features — LineString/Polygon can extend far off-screen
        if (f.geometry.type !== 'Point') continue
        if (classFilter && f.properties?.class !== classFilter) continue
        const name = (f.properties?.['name:latin'] ?? f.properties?.name) as string | undefined
        if (!name || seen.has(name)) continue
        const [lng, lat] = f.geometry.coordinates
        if (lat < bounds.getSouth() || lat > bounds.getNorth()) continue
        const w = bounds.getWest(), e = bounds.getEast()
        if (w <= e ? (lng < w || lng > e) : (lng < w && lng > e)) continue
        seen.add(name)
        names.push(name)
      }
    } catch { /* source may not be loaded yet */ }
    return names
  }

  getViewContext(): MapViewContext | null {
    if (!this.map) return null
    const center = this.map.getCenter()
    const bounds = this.map.getBounds()

    // Use querySourceFeatures so results are available regardless of label visibility
    const visibleCountries = this.queryNamesInBounds('place', bounds, 'country')
    const visibleOceans = this.queryNamesInBounds('water_name', bounds)

    return {
      center: { lat: center.lat, lng: center.lng },
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
      bounds: {
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth(),
      },
      visibleCountries,
      visibleOceans,
    }
  }

  // --- GeoJSON region highlighting ---

  private highlightCounter = 0

  /**
   * Highlight a GeoJSON region on the map.
   * Returns the layer ID for later removal.
   */
  highlightRegion(geojson: GeoJSON.GeoJSON, options?: { color?: string; opacity?: number }): string | null {
    // AYNI: the style itself, not every tile: `isStyleLoaded` also waits for every source, and a globe streaming data (or the flat map's Earth) nearly always has one loading (as ownLayersOnMap.ts found).
    if (!this.map || !styleReady(this.map)) return null
    const id = `highlight-${++this.highlightCounter}`
    const sourceId = `${id}-source`
    this.map.addSource(sourceId, { type: 'geojson', data: geojson })
    this.map.addLayer({
      id: `${id}-fill`,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': options?.color ?? 'rgba(250, 204, 21, 0.3)',
        'fill-opacity': options?.opacity ?? 0.3,
      },
    })
    this.map.addLayer({
      id: `${id}-outline`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': options?.color ?? '#facc15',
        'line-width': 2,
      },
    })
    return id
  }

  /** Remove a highlighted region by its ID. */
  removeHighlight(id: string): void {
    if (!this.map) return
    try { this.map.removeLayer(`${id}-fill`) } catch { /* noop */ }
    try { this.map.removeLayer(`${id}-outline`) } catch { /* noop */ }
    try { this.map.removeSource(`${id}-source`) } catch { /* noop */ }
  }

  /** Remove all highlighted regions. */
  clearHighlights(): void {
    if (!this.map) return
    for (let i = 1; i <= this.highlightCounter; i++) {
      this.removeHighlight(`highlight-${i}`)
    }
    this.highlightCounter = 0
  }

  // --- Disposal ---

  /**
   * Remove the map and clean up resources. MapLibre's `map.remove()`
   * tears down its internal DOM inside the caller-provided container,
   * but leaves the container element itself intact — ViewportManager
   * owns the container lifecycle.
   *
   * If this was the active (primary) renderer, clears the singleton
   * slot so screenshotService doesn't hand out a disposed reference.
   */
  dispose(): void {
    this.stopAutoRotate()
    // A panel can be torn down (a 4→1 layout change) before the style
    // ever settles, and a pending deadline would then build layers on a
    // removed map.
    if (this.styleDeadline) {
      clearTimeout(this.styleDeadline)
      this.styleDeadline = null
    }
    // Before map.remove(): the unsubscribe closures capture `map`, so
    // dropping the reference first would strand the handlers.
    this.clearLatLngCallbacks()
    this.clearTransect()
    this.clearBox()
    this.disarmPinPick()
    this.setPins([], () => {})
    this.clearRegionOutline()
    this.clearContours()
    // The probe sampler is page-shared and deliberately NOT disposed
    // here — other panels may still be using it, and tearing down its
    // context would take their readouts with it. Dropping the source
    // is enough: probeValueAt returns early without it.
    this.probeSource = null
    this.probeOptions = null
    this.applyDatasetZoom(null, null)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
    this.container = null
    if (activeRenderer === this) {
      activeRenderer = null
    }
  }
}
